import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getStackEntryById, deleteStackEntry, getRecipeById, getCookingSessions, createCookingSessionFromRecipe, getRecipeCookingSessions, getRecipeLineage, getCollections, addRecipeToCollection, removeRecipeFromCollection, updateRecipe, addRecipeToGroceryList, uploadImage } from '../services/api';
import { useAuth } from '../context/authContext';
import { getSubscriptionStatus } from '../services/subscriptionService';
import CookingCalendar from '../components/CookingCalendar';
import LineageViewerV2 from '../components/LineageViewerV2';
import MergeProposalModal from '../components/MergeProposalModal';
import { PageHeading, Section, Heading, Text, Modal } from '../components/ui';
import SignedImage from '../components/SignedImage';
import QuestionMarkIcon from '../components/QuestionMarkIcon';
import EducationModal from '../components/EducationModal';
import RecipeCardOverlay from '../components/RecipeCardOverlay';
import Toast from '../components/Toast';
import FloatingLiveSessionTab from '../components/FloatingLiveSessionTab';
import FloatingLineageTab from '../components/FloatingLineageTab';
import FloatingBackTab from '../components/FloatingBackTab';
import FloatingCompareTab from '../components/FloatingCompareTab';
import FloatingSessionsTab from '../components/FloatingSessionsTab';
import FloatingForkTab from '../components/FloatingForkTab';
import CompareSelectionModal from '../components/CompareSelectionModal';
import RecipeDiffModal from '../components/RecipeDiffModal';
import SessionsModal from '../components/SessionsModal';
import StepTimerPopover from '../components/StepTimerPopover';
import { useCompare } from '../context/CompareContext';
import { forkRecipe } from '../services/api';
import { logEvent, logError, logDebug } from '../utils/logger';
import MicrophonePermissionModal from '../components/MicrophonePermissionModal';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    notecardBlue: '#4A90E2', // Blue color for notecard dividing lines
    red: '#DC2626', // Red color for title separator
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

// Helper function to combine multi-component data with headers
const combineMultiComponentData = (recipe) => {
    if (!recipe) {
        return null;
    }
    
    if (!recipe.isMultiComponent) {
        return null;
    }
    
    if (!recipe.components || !Array.isArray(recipe.components) || recipe.components.length === 0) {
        return null;
    }

    const combinedIngredients = [];
    const combinedPrerequisites = [];
    const combinedSteps = [];

    // Add all component ingredients with headers
    recipe.components.forEach((component, compIndex) => {
        if (component.ingredients && Array.isArray(component.ingredients) && component.ingredients.length > 0) {
            const validIngredients = component.ingredients.filter(i => {
                const ing = typeof i === 'string' ? i : (i?.name || i?.text || String(i));
                return ing && ing.trim && ing.trim().length > 0;
            });
            
            if (validIngredients.length > 0) {
                combinedIngredients.push({
                    type: 'header',
                    text: `For the ${component.name}:`
                });
                validIngredients.forEach(ing => {
                    const ingText = typeof ing === 'string' ? ing : (ing?.name || ing?.text || String(ing));
                    combinedIngredients.push({
                        type: 'item',
                        text: ingText
                    });
                });
            }
        }
    });

    // Add all component prerequisites with headers
    recipe.components.forEach((component) => {
        if (component.prerequisites && component.prerequisites.length > 0) {
            const validPrereqs = component.prerequisites.filter(p => p && p.trim());
            if (validPrereqs.length > 0) {
                combinedPrerequisites.push({
                    type: 'header',
                    text: `For the ${component.name}:`
                });
                validPrereqs.forEach(prereq => {
                    combinedPrerequisites.push({
                        type: 'item',
                        text: prereq
                    });
                });
            }
        }
    });

    // Add assembly prerequisites if any (without header)
    if (recipe.assembly && recipe.assembly.prerequisites && recipe.assembly.prerequisites.length > 0) {
        const validAssemblyPrereqs = recipe.assembly.prerequisites.filter(p => p && p.trim());
        validAssemblyPrereqs.forEach(prereq => {
            combinedPrerequisites.push({
                type: 'item',
                text: prereq
            });
        });
    }

    // Add all component steps with headers
    recipe.components.forEach((component) => {
        if (component.steps && component.steps.length > 0) {
            const validSteps = component.steps
                .map(step => typeof step === 'string' ? step : step?.text || '')
                .filter(s => s && s.trim());
            if (validSteps.length > 0) {
                combinedSteps.push({
                    type: 'header',
                    text: `For the ${component.name}:`
                });
                validSteps.forEach(step => {
                    combinedSteps.push({
                        type: 'item',
                        text: step
                    });
                });
            }
        }
    });

    // Add assembly steps at the end (without header)
    if (recipe.assembly && recipe.assembly.steps && recipe.assembly.steps.length > 0) {
        const validAssemblySteps = recipe.assembly.steps
            .map(step => typeof step === 'string' ? step : step?.text || '')
            .filter(s => s && s.trim());
        validAssemblySteps.forEach(step => {
            combinedSteps.push({
                type: 'item',
                text: step
            });
        });
    }

    return {
        ingredients: combinedIngredients,
        prerequisites: combinedPrerequisites,
        steps: combinedSteps
    };
};

const RecipeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();

    const [recipe, setRecipe] = useState(null);
    const [originalRecipe, setOriginalRecipe] = useState(null);
    const [originalRecipePrivate, setOriginalRecipePrivate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [reflectionModalOpen, setReflectionModalOpen] = useState(false);
    const [calendarModalOpen, setCalendarModalOpen] = useState(false);
    const [sessionData, setSessionData] = useState(null);
    const [recipeSessions, setRecipeSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [startingSession, setStartingSession] = useState(false);
    const [showCustomizeModal, setShowCustomizeModal] = useState(false);
    const [customIngredients, setCustomIngredients] = useState([]);
    const [customInstructions, setCustomInstructions] = useState(null);
    const [customizing, setCustomizing] = useState(false);
    const [customRecipeName, setCustomRecipeName] = useState('');
    const [customIsMultiComponent, setCustomIsMultiComponent] = useState(false);
    const [customComponents, setCustomComponents] = useState([]);
    const [customAssembly, setCustomAssembly] = useState({ prerequisites: [], steps: [] });
    const [showPrerequisites, setShowPrerequisites] = useState(false);
    const [showSteps, setShowSteps] = useState(false);
    const [selectedReflectionSession, setSelectedReflectionSession] = useState(null);
    const [showSessionReflectionModal, setShowSessionReflectionModal] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [lineage, setLineage] = useState(null);
    const [lineageLoading, setLineageLoading] = useState(false);
    const [showLineageModal, setShowLineageModal] = useState(false);
    const [lineageCompareMode, setLineageCompareMode] = useState(false);
    const [lineageRefreshKey, setLineageRefreshKey] = useState(0);
    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [showForkModal, setShowForkModal] = useState(false);
    const [forkIngredients, setForkIngredients] = useState([]);
    const [forkInstructions, setForkInstructions] = useState({ prerequisites: [], steps: [] });
    const [forkRecipeName, setForkRecipeName] = useState('');
    const [forking, setForking] = useState(false);
    const [forkIsMultiComponent, setForkIsMultiComponent] = useState(false);
    const [forkComponents, setForkComponents] = useState([]);
    const [forkAssembly, setForkAssembly] = useState({ prerequisites: [], steps: [] });
    const [recipeToFork, setRecipeToFork] = useState(null); // Track which recipe is being forked
    
    // Compare functionality
    const {
        showCompareSelectionModal,
        openCompareSelection,
        closeCompareSelection,
        selectedRecipes: compareSelectedRecipes,
        toggleRecipeSelection,
        compareRecipes,
        showCompareModal,
        closeCompareModal
    } = useCompare();
    
    // Collection state
    const [collections, setCollections] = useState([]);
    const [showAddToCollection, setShowAddToCollection] = useState(false);
    
    // Merge proposal state
    const [showMergeProposalModal, setShowMergeProposalModal] = useState(false);
    const [showUpdateGoldStandardModal, setShowUpdateGoldStandardModal] = useState(false);
    
    // Privacy controls
    const isOwner = recipe && user && recipe.owner_id === user.uid;
    const [mergeProposalSuccess, setMergeProposalSuccess] = useState(false);
    
    // Image editing state
    const [editingImageIndex, setEditingImageIndex] = useState(null);
    const [imageDragOver, setImageDragOver] = useState(null);
    const [updatingImage, setUpdatingImage] = useState(false);
    
    // Recipe card overlay state
    const [showRecipeCardOverlay, setShowRecipeCardOverlay] = useState(false);
    
    // Education modal states
    const [showStartSessionModal, setShowStartSessionModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showLineageEducationModal, setShowLineageEducationModal] = useState(false);
    const [showCalendarEducationModal, setShowCalendarEducationModal] = useState(false);
    const [showMenuDropdown, setShowMenuDropdown] = useState(false);
    const [showAttributionPopup, setShowAttributionPopup] = useState(false);
    const [selectedLineageRecipe, setSelectedLineageRecipe] = useState(null);
    
    // Timer popover state
    const [openTimerPopover, setOpenTimerPopover] = useState(null); // { stepIndex, stepText }
    
    // Premium subscription state
    const [subscription, setSubscription] = useState(null);
    const [addingToGroceryList, setAddingToGroceryList] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [showMicrophonePermissionModal, setShowMicrophonePermissionModal] = useState(false);
    const [pendingSessionStart, setPendingSessionStart] = useState(null); // Store session start params

    useEffect(() => {
        if (id) {
            fetchRecipeDetails();
        }
    }, [id]);
    
    useEffect(() => {
        if (user) {
            const fetchSubscription = async () => {
                try {
                    const status = await getSubscriptionStatus();
                    setSubscription(status);
                } catch (error) {
                    console.error('Failed to fetch subscription status:', error);
                }
            };
            fetchSubscription();
        }
    }, [user]);


    useEffect(() => {
        if (user) {
            fetchCollections();
        }
    }, [user]);

    // Close mobile menu and dropdown menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileMenuOpen && !event.target.closest('.mobile-menu-container')) {
                setMobileMenuOpen(false);
            }
            if (showMenuDropdown && !event.target.closest('.menu-dropdown-container')) {
                setShowMenuDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [mobileMenuOpen]);


    // Keyboard navigation for fork lineage
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (lineage && lineage.recipes && lineage.recipes.length > 1) {
                if (event.key === 'ArrowLeft' && getCurrentRecipeIndex() > 0) {
                    event.preventDefault();
                    navigateToPrevious();
                } else if (event.key === 'ArrowRight' && getCurrentRecipeIndex() < lineage.recipes.length - 1) {
                    event.preventDefault();
                    navigateToNext();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [lineage, id]);

    const fetchRecipeDetails = async () => {
        try {
            setLoading(true);
            const recipeData = await getRecipeById(id);
            
            // Parse ingredients if they're JSON strings
            if (typeof recipeData.ingredients === 'string') {
                recipeData.ingredients = JSON.parse(recipeData.ingredients || '[]');
            }

            // Parse instructions if they're JSON strings
            if (typeof recipeData.instructions === 'string') {
                recipeData.instructions = JSON.parse(recipeData.instructions || '{}');
            }

            // Parse components if they're JSON strings (for multi-component recipes)
            if (recipeData.isMultiComponent) {
                if (typeof recipeData.components === 'string') {
                    try {
                        recipeData.components = JSON.parse(recipeData.components || '[]');
                    } catch (e) {
                        console.error('Error parsing components:', e);
                        recipeData.components = [];
                    }
                }
                
                // Parse assembly if it's a JSON string
                if (typeof recipeData.assembly === 'string') {
                    try {
                        recipeData.assembly = JSON.parse(recipeData.assembly || '{}');
                    } catch (e) {
                        console.error('Error parsing assembly:', e);
                        recipeData.assembly = { prerequisites: [], steps: [] };
                    }
                } else if (!recipeData.assembly) {
                    recipeData.assembly = { prerequisites: [], steps: [] };
                }
            }

            console.log('📋 Recipe data after parsing:', {
                isMultiComponent: recipeData.isMultiComponent,
                componentCount: recipeData.components?.length || 0,
                hasAssembly: !!recipeData.assembly,
                components: recipeData.components
            });

                    setRecipe(recipeData);
        
        // Fetch recipe sessions for this recipe (only if user is logged in)
        if (user) {
            await fetchRecipeSessions();
        }
        
        // Fetch fork lineage
        await fetchForkLineage();
        
        // Fetch session data if this recipe was created from a cooking session (only if user is logged in)
        if (recipeData.reflection && user) {
            try {
                const reflection = JSON.parse(recipeData.reflection);
                if (reflection.cooking_session_id) {
                    await fetchSessionData(reflection.cooking_session_id);
                }
            } catch (e) {
                console.error('Error parsing reflection data:', e);
            }
        }

            // If this is a fork, try to fetch the original recipe
            if (!recipeData.is_original && recipeData.parent_id) {
                try {
                    const originalData = await getStackEntryById(recipeData.parent_id);
                    setOriginalRecipe(originalData);
                    setOriginalRecipePrivate(false);
                } catch (err) {
                    // Original recipe might be private or deleted
                    console.warn('Could not fetch original recipe:', err);
                    setOriginalRecipePrivate(true);
                }
            }
        } catch (err) {
            console.error('Error fetching recipe details:', err);
            setError('Failed to load recipe details');
        } finally {
            setLoading(false);
        }
    };

    const fetchSessionData = async (sessionId) => {
        try {
            const token = await user.getIdToken();
            const sessions = await getCookingSessions(token);
            const session = sessions.find(s => s.id === sessionId);
            
            if (session && session.actual_duration_seconds) {
                setSessionData(session);
            }
        } catch (error) {
            console.error('Error fetching session data:', error);
        }
    };

    const fetchRecipeSessions = async () => {
        try {
            const token = await user.getIdToken();
            const sessions = await getRecipeCookingSessions(id, token);
            setRecipeSessions(sessions);
            
            // Check for active (incomplete) sessions
            const activeSession = sessions.find(session => !session.was_completed);
            setActiveSession(activeSession || null);
        } catch (error) {
            console.error('Error fetching recipe sessions:', error);
        }
    };

    const fetchForkLineage = async () => {
        try {
            setLineageLoading(true);
            const lineageData = await getRecipeLineage(id);
            setLineage(lineageData);
        } catch (error) {
            console.error('Error fetching fork lineage:', error);
            // Don't set error state for lineage as it's not critical
        } finally {
            setLineageLoading(false);
        }
    };

    const proceedWithSessionStart = async () => {
        try {
            setStartingSession(true);
            setCustomizing(true);
            const token = await user.getIdToken();
            
            const pending = pendingSessionStart;
            
            // If there's an active session and no custom data, navigate directly to it
            if (activeSession && (!pending || pending.type === 'simple')) {
                logDebug('[RecipeDetails]', 'Using existing active session', { sessionId: activeSession.id });
                navigate(`/live/recipe/${id}`);
                return;
            }
            
            let session;
            
            if (pending && pending.type === 'custom' && pending.customData) {
                // Handle custom recipe modifications (from customize modal)
                const { ingredients, instructions, isMultiComponent, components, assembly } = pending.customData;
                
                // Check if there are actual modifications
                const hasModifications = ingredients || instructions || 
                    (isMultiComponent && components && components.length > 0) ||
                    (assembly && (assembly.prerequisites?.length > 0 || assembly.steps?.length > 0));
                
                if (hasModifications) {
                    // Create fork for ANY modifications (regardless of ownership)
                    logDebug('[RecipeDetails]', 'Creating fork due to modifications');
                    const ingredientsToPass = ingredients || null;
                    const instructionsToPass = instructions || null;
                    
                    const res = await createCookingSessionFromRecipe(
                        id,
                        token,
                        ingredientsToPass,
                        instructionsToPass
                    );
                    logEvent('session.started', {
                        sessionId: res.id,
                        recipeId: res.recipe_id,
                        forkId: res.fork_id || res.fork_recipe_id
                    });
                    logDebug('[RecipeDetails]', 'Fork created with modifications:', {
                        sessionId: res.id,
                        recipeId: res.recipe_id,
                        forkId: res.fork_id,
                        forkRecipeId: res.fork_recipe_id,
                        stackId: res.stack_id,
                        forkState: res.fork_state
                    });
                    const forkRecipeId = res.fork_recipe_id || res.recipe_id || res.stack_id || id;
                    logDebug('[RecipeDetails]', 'Navigating to live session with fork recipe ID:', forkRecipeId);
                    navigate(`/live/recipe/${forkRecipeId}`);
                    setShowCustomizeModal(false);
                    return;
                } else {
                    // No modifications - start session directly
                    session = await createCookingSessionFromRecipe(
                        id,
                        token,
                        null,
                        null
                    );
                }
            } else {
                // Otherwise, create a new session (backend will handle existing session check)
                session = await createCookingSessionFromRecipe(id, token);
            }
            
            logEvent('session.started', {
                sessionId: session.id,
                recipeId: id
            });
            logDebug('[RecipeDetails]', 'Session created', { sessionId: session.id, recipeId: id });
            
            // Store session ID in sessionStorage so LiveSession knows not to create another
            sessionStorage.setItem('pendingLiveSessionId', session.id);
            
            // Navigate to live session
            navigate(`/live/recipe/${id}`);
            
            if (pending && pending.type === 'custom') {
                setShowCustomizeModal(false);
            }
        } catch (error) {
            logError('[RecipeDetails]', 'Error starting live session', error);
            logEvent('session.start.failed', {
                recipeId: id,
                error: error.message || 'Unknown error'
            });
            alert('Failed to start live session. Please try again.');
        } finally {
            setStartingSession(false);
            setCustomizing(false);
            setShowMicrophonePermissionModal(false);
            setPendingSessionStart(null);
        }
    };

    const handleStartLiveSession = async () => {
        // Show microphone permission modal first
        setPendingSessionStart({ type: 'simple' });
        setShowMicrophonePermissionModal(true);
    };

    // Reset image index when recipe changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [recipe]);

    // Add/remove modal-open class to body when lineage modal opens/closes
    useEffect(() => {
        if (showLineageModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
            // Close fork modal when lineage modal closes
            if (showForkModal) {
                setShowForkModal(false);
                setRecipeToFork(null); // Clear the recipe to fork when lineage modal closes
            }
        }

        // Cleanup function to remove class when component unmounts
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showLineageModal, showForkModal]);

    const handleDelete = async () => {
        try {
            if (!user) throw new Error('User not authenticated');
            const token = await user.getIdToken();
            await deleteStackEntry(id, token);
            navigate('/recipes');
        } catch (err) {
            console.error('Error deleting recipe:', err);
            alert('Failed to delete recipe. Please try again.');
        }
    };

    const nextImage = () => {
        if (recipe.images && recipe.images.length > 1) {
            setCurrentImageIndex((prev) => (prev + 1) % recipe.images.length);
        }
    };

    const prevImage = () => {
        if (recipe.images && recipe.images.length > 1) {
            setCurrentImageIndex((prev) => (prev - 1 + recipe.images.length) % recipe.images.length);
        }
    };

    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };

    const navigateToParent = () => {
        if (lineage && lineage.parent) {
            navigate(`/recipe/${lineage.parent.id}`);
        }
    };

    const navigateToChild = (index = 0) => {
        if (lineage && lineage.children && lineage.children.length > index) {
            navigate(`/recipe/${lineage.children[index].id}`);
        }
    };

    const getCurrentRecipeIndex = () => {
        if (!lineage || !lineage.recipes) return -1;
        return lineage.recipes.findIndex(r => r.id === parseInt(id));
    };

    const navigateToPrevious = () => {
        const currentIndex = getCurrentRecipeIndex();
        if (currentIndex > 0) {
            navigate(`/recipe/${lineage.recipes[currentIndex - 1].id}`);
        }
    };

    const navigateToNext = () => {
        const currentIndex = getCurrentRecipeIndex();
        if (currentIndex < lineage.recipes.length - 1) {
            navigate(`/recipe/${lineage.recipes[currentIndex + 1].id}`);
        }
    };

    const fetchCollections = async () => {
        try {
            const token = await user.getIdToken();
            const collectionsData = await getCollections(token);
            setCollections(collectionsData);
        } catch (error) {
            console.error('Error fetching collections:', error);
            // Don't set error state for collections as it's not critical
        }
    };

    // Collection management functions
    const addToCollection = async (collectionId) => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await addRecipeToCollection(collectionId, recipe.id, token);
            setRecipe(prev => ({ ...prev, collection_id: collectionId }));
            setShowAddToCollection(false);
            
            // Refetch the recipe to ensure we have the latest data
            await fetchRecipeDetails();
        } catch (err) {
            console.error('Error adding recipe to collection:', err);
            alert(`Failed to add recipe to collection: ${err.message || 'Please try again.'}`);
        }
    };

    const removeFromCollection = async () => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await removeRecipeFromCollection(recipe.id, token);
            setRecipe(prev => ({ ...prev, collection_id: null }));
            
            // Refetch the recipe to ensure we have the latest data
            await fetchRecipeDetails();
        } catch (err) {
            console.error('Error removing recipe from collection:', err);
            alert('Failed to remove recipe from collection. Please try again.');
        }
    };

    const handleReplaceImage = async (imageIndex, file) => {
        if (!user || !recipe || !isOwner) return;
        
        try {
            setUpdatingImage(true);
            const token = await user.getIdToken();
            
            // Validate file
            if (!file.type.startsWith('image/')) {
                alert('Please select a valid image file');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert('File is too large (max 10MB)');
                return;
            }
            
            // Upload new image
            const newImageUrl = await uploadImage(file, token);
            
            // Update recipe with new image at the specified index
            const updatedImages = [...recipe.images];
            updatedImages[imageIndex] = newImageUrl;
            
            const formData = new FormData();
            formData.append('name', recipe.name || '');
            formData.append('category', recipe.category || '');
            formData.append('cooking_time', recipe.cooking_time || '');
            formData.append('isMultiComponent', recipe.isMultiComponent || false);
            // Use existingImages to pass the array of image URLs (backend expects this field)
            formData.append('existingImages', JSON.stringify(updatedImages));
            
            // Preserve other recipe data
            if (recipe.ingredients) {
                formData.append('ingredients', JSON.stringify(recipe.ingredients));
            }
            if (recipe.instructions) {
                formData.append('instructions', JSON.stringify(recipe.instructions));
            }
            if (recipe.components) {
                formData.append('components', JSON.stringify(recipe.components));
            }
            if (recipe.assembly) {
                formData.append('assembly', JSON.stringify(recipe.assembly));
            }
            
            await updateRecipe(recipe.id, formData, token);
            
            // Refresh recipe data
            await fetchRecipeDetails();
            setEditingImageIndex(null);
        } catch (error) {
            console.error('Error replacing image:', error);
            alert('Failed to replace image. Please try again.');
        } finally {
            setUpdatingImage(false);
        }
    };

    const handleImageFileSelect = (imageIndex) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            if (e.target.files && e.target.files.length > 0) {
                handleReplaceImage(imageIndex, e.target.files[0]);
            }
        };
        input.click();
    };

    const handleImageDrop = (e, imageIndex) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDragOver(null);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleReplaceImage(imageIndex, e.dataTransfer.files[0]);
        }
    };

    const handleImageDragOver = (e, imageIndex) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDragOver(imageIndex);
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDragOver(null);
    };

    const handleAddToGroceryList = async () => {
        if (!user || !recipe) return;
        
        const isPremiumUser = subscription && ['unlimited', 'beta', 'admin', 'family'].includes(subscription.planType);
        
        if (!isPremiumUser) {
            // Show upgrade prompt or redirect to subscription page
            const upgrade = window.confirm('This feature requires a premium subscription. Would you like to upgrade?');
            if (upgrade) {
                navigate('/subscription');
            }
            setShowMenuDropdown(false);
            return;
        }
        
        try {
            setAddingToGroceryList(true);
            // Keep dropdown open to show loading state
            const token = await user.getIdToken();
            
            // Add recipe to grocery list (default servings will be used)
            const response = await addRecipeToGroceryList(recipe.id, null, token);
            const groceryList = response.list || response;
            
            // Close dropdown after successful addition
            setShowMenuDropdown(false);
            
            // Show success toast
            setToastType('success');
            setToastMessage(`✓ Added "${recipe.name || recipe.title || 'recipe'}" to grocery list`);
            
            // Wait a moment for toast to show, then navigate
            setTimeout(() => {
                navigate('/grocery/generate', { state: { groceryList } });
            }, 1500);
        } catch (err) {
            console.error('Error adding recipe to grocery list:', err);
            // Close dropdown on error
            setShowMenuDropdown(false);
            alert(`Failed to add recipe to grocery list: ${err.message || 'Please try again.'}`);
        } finally {
            setAddingToGroceryList(false);
        }
    };

    // Check if user is the only contributor to this recipe lineage
    const isOnlyContributor = () => {
        if (!recipe || !user) return false;
        
        // If this is the original recipe and user owns it, they are the only contributor
        if (recipe.is_original && recipe.owner_id === user.uid) {
            return true;
        }
        
        // If this is a fork and user owns it, check if they are the only contributor
        if (!recipe.is_original && recipe.owner_id === user.uid) {
            // For now, assume they are the only contributor if they own the fork
            // TODO: Implement proper lineage checking to see if others have contributed
            return true;
        }
        
        return false;
    };

    // Merge proposal handlers
    const handleProposeMerge = () => {
        if (isOnlyContributor()) {
            // Show simple confirmation modal for single contributor
            setShowUpdateGoldStandardModal(true);
        } else {
            // Show full proposal modal for multiple contributors
            setShowMergeProposalModal(true);
        }
    };

    const handleProposalCreated = (proposal) => {
        setMergeProposalSuccess(true);
        setShowMergeProposalModal(false);
        
        // Hide success message after 5 seconds
        setTimeout(() => {
            setMergeProposalSuccess(false);
        }, 5000);
    };


    // Add this formatting function
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'Not specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // When user clicks start (new session), open customization modal
    const handleStartLiveSessionClick = () => {
        const isMultiComponent = recipe.isMultiComponent === true;
        setCustomIsMultiComponent(isMultiComponent);
        
        if (isMultiComponent && recipe.components && recipe.assembly) {
            // Multi-component recipe
            const components = Array.isArray(recipe.components) ? recipe.components : [];
            const assembly = recipe.assembly || { prerequisites: [], steps: [] };
            
            setCustomComponents(components.map(comp => ({
                ...comp,
                ingredients: Array.isArray(comp.ingredients) ? [...comp.ingredients] : [],
                prerequisites: Array.isArray(comp.prerequisites) ? [...comp.prerequisites] : [],
                steps: Array.isArray(comp.steps) ? comp.steps.map(s => typeof s === 'string' ? s : s.text || '') : []
            })));
            setCustomAssembly({
                prerequisites: Array.isArray(assembly.prerequisites) ? [...assembly.prerequisites] : [],
                steps: Array.isArray(assembly.steps) ? assembly.steps.map(s => typeof s === 'string' ? s : s.text || '') : []
            });
            // Clear simple mode fields
            setCustomIngredients([]);
            setCustomInstructions({ prerequisites: [], steps: [] });
        } else {
            // Simple recipe
        const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
        const instructions = recipe.instructions || {};
        
        setCustomIngredients([...ingredients]);
        setCustomInstructions({ ...instructions });
            // Clear multi-component fields
            setCustomComponents([]);
            setCustomAssembly({ prerequisites: [], steps: [] });
        }
        
        setCustomRecipeName(recipe.name || recipe.title || '');
        setShowCustomizeModal(true);
    };

    // When user clicks fork tab, open fork modal
    const handleForkClick = async () => {
        // CRITICAL: Use selectedLineageRecipe if in lineage modal, otherwise use current recipe
        let targetRecipe = recipe;
        
        if (showLineageModal && selectedLineageRecipe) {
            // In lineage modal with a selected recipe - use the selected one
            logDebug('[RecipeDetails]', 'Fork click: Using selected lineage recipe', { 
                selectedId: selectedLineageRecipe.id, 
                selectedName: selectedLineageRecipe.name,
                currentRecipeId: recipe?.id 
            });
            
            // Fetch full recipe details if selectedLineageRecipe doesn't have all data
            if (!selectedLineageRecipe.ingredients && !selectedLineageRecipe.components) {
                try {
                    const token = await user.getIdToken();
                    const fullRecipe = await getRecipeById(selectedLineageRecipe.id);
                    targetRecipe = fullRecipe;
                    logDebug('[RecipeDetails]', 'Fetched full recipe details for fork:', { 
                        recipeId: fullRecipe.id,
                        recipeName: fullRecipe.name,
                        imageCount: Array.isArray(fullRecipe.images) ? fullRecipe.images.length : 0
                    });
                } catch (error) {
                    logError('[RecipeDetails]', 'Error fetching selected recipe details', error);
                    alert('Failed to load recipe details. Please try again.');
                    return;
                }
            } else {
                targetRecipe = selectedLineageRecipe;
            }
        } else if (!recipe) {
            logError('[RecipeDetails]', 'Cannot fork: recipe is not loaded');
            return;
        }
        
        // Store which recipe we're forking
        setRecipeToFork(targetRecipe);
        
        logDebug('[RecipeDetails]', 'Fork click handler called', { 
            recipeId: targetRecipe.id, 
            recipeName: targetRecipe.name,
            isSelectedLineageRecipe: showLineageModal && selectedLineageRecipe?.id === targetRecipe.id
        });
        
        const isMultiComponent = targetRecipe.isMultiComponent === true;
        setForkIsMultiComponent(isMultiComponent);
        
        if (isMultiComponent && targetRecipe.components && targetRecipe.assembly) {
            // Multi-component recipe
            const components = Array.isArray(targetRecipe.components) ? targetRecipe.components : [];
            const assembly = targetRecipe.assembly || { prerequisites: [], steps: [] };
            
            setForkComponents(components.map(comp => ({
                ...comp,
                ingredients: Array.isArray(comp.ingredients) ? [...comp.ingredients] : [],
                prerequisites: Array.isArray(comp.prerequisites) ? [...comp.prerequisites] : [],
                steps: Array.isArray(comp.steps) ? comp.steps.map(s => typeof s === 'string' ? s : s.text || '') : []
            })));
            setForkAssembly({
                prerequisites: Array.isArray(assembly.prerequisites) ? [...assembly.prerequisites] : [],
                steps: Array.isArray(assembly.steps) ? assembly.steps.map(s => typeof s === 'string' ? s : s.text || '') : []
            });
            // Clear simple mode fields
            setForkIngredients([]);
            setForkInstructions({ prerequisites: [], steps: [] });
        } else {
            // Simple recipe
            const ingredients = Array.isArray(targetRecipe.ingredients) ? targetRecipe.ingredients : [];
            const instructions = targetRecipe.instructions || { prerequisites: [], steps: [] };
        
        setForkIngredients([...ingredients]);
        setForkInstructions({ 
            prerequisites: [...(instructions.prerequisites || [])],
            steps: [...(instructions.steps || [])]
        });
            // Clear multi-component fields
            setForkComponents([]);
            setForkAssembly({ prerequisites: [], steps: [] });
        }
        
        setForkRecipeName(targetRecipe.name || targetRecipe.title || '');
        console.log('🔀 Setting showForkModal to true');
        setShowForkModal(true);
        
        // Force a re-render check
        setTimeout(() => {
            console.log('🔀 Modal state check:', { showForkModal: true, recipeExists: !!targetRecipe, recipeId: targetRecipe.id });
        }, 100);
    };

    // Handle fork creation
    const handleCreateFork = async () => {
        // CRITICAL: Use recipeToFork (set in handleForkClick) or fall back to recipe
        const targetRecipe = recipeToFork || recipe;
        
        if (!user || !targetRecipe) {
            logError('[RecipeDetails]', 'Cannot create fork: missing user or recipe', { 
                hasUser: !!user, 
                hasRecipe: !!targetRecipe,
                recipeToForkId: recipeToFork?.id,
                recipeId: recipe?.id
            });
            return;
        }
        
        try {
            setForking(true);
            const token = await user.getIdToken();
            
            logDebug('[RecipeDetails]', 'Creating fork from recipe:', { 
                recipeId: targetRecipe.id, 
                recipeName: targetRecipe.name,
                isSelectedLineageRecipe: recipeToFork?.id === selectedLineageRecipe?.id
            });
            
            const forkData = {
                name: forkRecipeName,
                category: targetRecipe.category,
                cooking_time: targetRecipe.cooking_time,
                isMultiComponent: forkIsMultiComponent,
                change_summary: 'Forked recipe'
            };
            
            if (forkIsMultiComponent) {
                // Multi-component fork
                forkData.components = forkComponents;
                forkData.assembly = forkAssembly;
                forkData.ingredients = [];
                forkData.instructions = { prerequisites: [], steps: [] };
            } else {
                // Simple fork
                forkData.ingredients = forkIngredients;
                forkData.instructions = forkInstructions;
            }
            
            // CRITICAL: Use the target recipe's ID, not the current page recipe's ID
            const forkedRecipe = await forkRecipe(targetRecipe.id, forkData, token);
            
            logEvent('recipe.fork.created', { 
                forkId: forkedRecipe.id,
                sourceRecipeId: targetRecipe.id,
                name: forkRecipeName
            });
            logDebug('[RecipeDetails]', 'Fork created successfully:', { 
                forkedRecipeId: forkedRecipe.id,
                forkedFromId: targetRecipe.id
            });
            
            // Clear the recipe to fork state
            setRecipeToFork(null);
            
            // Refresh lineage and navigate to the new fork
            setLineageRefreshKey(prev => prev + 1);
            setShowForkModal(false);
            navigate(`/recipes/${forkedRecipe.id}`);
        } catch (error) {
            logError('[RecipeDetails]', 'Error creating fork', error);
            logEvent('recipe.fork.failed', {
                sourceRecipeId: targetRecipe?.id,
                error: error.message || 'Unknown error'
            });
            alert('Failed to create fork. Please try again.');
        } finally {
            setForking(false);
        }
    };

    // Helper function to check if ingredients have been modified
    const haveIngredientsChanged = (original, custom) => {
        if (!original || !custom) return false;
        
        // Handle arrays properly
        const originalArray = Array.isArray(original) ? original : [];
        const customArray = Array.isArray(custom) ? custom : [];
        
        if (originalArray.length !== customArray.length) return true;
        
        // Check if any ingredient text has changed
        for (let i = 0; i < originalArray.length; i++) {
            if (originalArray[i] !== customArray[i]) return true;
        }
        return false;
    };

    // Helper function to check if instructions have been modified
    const haveInstructionsChanged = (original, custom) => {
        if (!original || !custom) return false;
        
        // Check prerequisites - handle both cases where they exist or don't exist
        const originalPrereqs = original.prerequisites || [];
        const customPrereqs = custom.prerequisites || [];
        
        if (originalPrereqs.length !== customPrereqs.length) return true;
        for (let i = 0; i < originalPrereqs.length; i++) {
            if (originalPrereqs[i] !== customPrereqs[i]) return true;
        }
        
        // Check steps - handle both cases where they exist or don't exist
        const originalSteps = original.steps || [];
        const customSteps = custom.steps || [];
        
        if (originalSteps.length !== customSteps.length) return true;
        for (let i = 0; i < originalSteps.length; i++) {
            if (originalSteps[i] !== customSteps[i]) return true;
        }
        
        return false;
    };

    // Helper function to normalize step data (handle both string and object formats)
    const normalizeSteps = (steps) => {
        if (!steps || !Array.isArray(steps)) return [];
        return steps.map(step => {
            if (typeof step === 'string') return step;
            if (step && typeof step === 'object') return step.text || JSON.stringify(step);
            return String(step);
        });
    };

    // Helper function to check if multi-component data has changed
    const haveMultiComponentChanged = (original, custom) => {
        if (!original || !custom) return false;
        
        // Check components
        if (original.components?.length !== custom.components?.length) {
            console.log('🔍 Multi-component change: component count different', {
                originalCount: original.components?.length,
                customCount: custom.components?.length
            });
            return true;
        }
        
        for (let i = 0; i < (original.components?.length || 0); i++) {
            const origComp = original.components[i];
            const customComp = custom.components[i];
            
            if (origComp.name !== customComp.name) {
                console.log('🔍 Multi-component change: component name different', {
                    index: i,
                    originalName: origComp.name,
                    customName: customComp.name
                });
                return true;
            }
            
            if (JSON.stringify(origComp.ingredients) !== JSON.stringify(customComp.ingredients)) {
                console.log('🔍 Multi-component change: ingredients different', {
                    index: i,
                    original: origComp.ingredients,
                    custom: customComp.ingredients
                });
                return true;
            }
            
            // Normalize steps before comparing (handle both string and object formats)
            const origSteps = normalizeSteps(origComp.steps);
            const customSteps = normalizeSteps(customComp.steps);
            if (JSON.stringify(origSteps) !== JSON.stringify(customSteps)) {
                console.log('🔍 Multi-component change: steps different', {
                    index: i,
                    originalSteps: origSteps,
                    customSteps: customSteps
                });
                return true;
            }
        }
        
        // Check assembly - also normalize steps
        const origAssemblySteps = normalizeSteps(original.assembly?.steps);
        const customAssemblySteps = normalizeSteps(custom.assembly?.steps);
        if (JSON.stringify(origAssemblySteps) !== JSON.stringify(customAssemblySteps)) {
            console.log('🔍 Multi-component change: assembly steps different', {
                original: origAssemblySteps,
                custom: customAssemblySteps
            });
            return true;
        }
        
        if (JSON.stringify(original.assembly?.prerequisites || []) !== JSON.stringify(custom.assembly?.prerequisites || [])) {
            console.log('🔍 Multi-component change: assembly prerequisites different', {
                original: original.assembly?.prerequisites,
                custom: custom.assembly?.prerequisites
            });
            return true;
        }
        
        console.log('✅ No multi-component changes detected');
        return false;
    };

    // When user confirms customization, start session (with or without fork)
    const handleConfirmCustomization = async () => {
        // Prepare custom data first
        let hasModifications = false;
        let ingredientsToPass = null;
        let instructionsToPass = null;
        
        if (customIsMultiComponent) {
            // Multi-component recipe
            const originalComponents = recipe.components || [];
            const originalAssembly = recipe.assembly || { prerequisites: [], steps: [] };
            
            hasModifications = haveMultiComponentChanged(
                { components: originalComponents, assembly: originalAssembly },
                { components: customComponents, assembly: customAssembly }
            );
            
            // For multi-component, we need to pass components and assembly
            instructionsToPass = {
                isMultiComponent: true,
                components: customComponents,
                assembly: customAssembly,
                prerequisites: [],
                steps: [] // Steps will be built from components + assembly
            };
            ingredientsToPass = []; // All ingredients are in components
        } else {
            // Simple recipe
            const ingredientsChanged = haveIngredientsChanged(recipe.ingredients, customIngredients);
            const instructionsChanged = haveInstructionsChanged(recipe.instructions, customInstructions);
            hasModifications = ingredientsChanged || instructionsChanged;
            
            ingredientsToPass = customIngredients;
            instructionsToPass = customInstructions;
        }
        
        logDebug('[RecipeDetails]', 'Customization Debug:', {
            isOwner,
            recipeId: id,
            isMultiComponent: customIsMultiComponent,
            hasModifications,
            ingredientsToPass,
            instructionsToPass
        });
        
        // Close the customize modal first
        setShowCustomizeModal(false);
        
        // Store custom data and show microphone permission modal
        setPendingSessionStart({ 
            type: 'custom',
            customData: {
                ingredients: ingredientsToPass,
                instructions: instructionsToPass,
                isMultiComponent: customIsMultiComponent,
                components: customComponents,
                assembly: customAssembly
            }
        });
        setShowMicrophonePermissionModal(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading recipe...</Text>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Error</Heading>
                        <Text>{error}</Text>
                        <Link to="/recipes" className="nav-link mt-4 inline-block">BACK TO RECIPES</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Recipe Not Found</Heading>
                        <Text>The recipe you're looking for doesn't exist or has been removed.</Text>
                        <Link to="/recipes" className="nav-link mt-4 inline-block">BACK TO RECIPES</Link>
                    </div>
                </div>
            </div>
        );
    }


    const pageContainerClass = 'min-h-screen bg-neutral-50';
    const innerContainerClass = 'w-full px-0 py-8';
    const cardWrapperClass = 'w-full flex justify-center px-4';
    const cardInnerWrapperClass = 'w-full max-w-[630px] md:max-w-[1000px] xl:max-w-[1200px] relative group';
    const cardClass = 'bg-neutral-0 hairline p-6 relative z-10 transition-all duration-200';
    const contentGridClass = 'grid grid-cols-1 lg:grid-cols-2 gap-6 h-full';

    return (
        <>
            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage('')}
                    duration={3000}
                />
            )}
            
            <MicrophonePermissionModal
                isOpen={showMicrophonePermissionModal}
                onPermissionGranted={proceedWithSessionStart}
                onCancel={() => {
                    setShowMicrophonePermissionModal(false);
                    setPendingSessionStart(null);
                    setCustomizing(false);
                }}
            />
        {/* Fork Recipe Modal - Render at top level, outside main content */}
        {/* CRITICAL: Use recipeToFork if set (from lineage selection), otherwise use recipe */}
        {showForkModal && (recipeToFork || recipe) && createPortal(
            <div 
                 className="fixed inset-0 flex items-center justify-center p-3 md:p-6 mobile-safe" 
                 style={{ 
                     backgroundColor: 'rgba(0, 0, 0, 0.5)',
                     position: 'fixed',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: 0,
                     zIndex: 1000000 // Higher than walkthrough overlay (z-30) and chef tab (1000000)
                 }}
                 onClick={() => {
                     console.log('🔀 Backdrop clicked, closing modal');
                     setShowForkModal(false);
                     setRecipeToFork(null); // Clear the recipe to fork when closing
                 }}
            >
                <div 
                    className="bg-neutral-0 hairline max-w-[1150px] w-full p-0"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-neutral-0 relative w-full overflow-x-hidden">
                        {/* Close Button - Floating Top Right */}
                        <button 
                            onClick={() => {
                                setShowForkModal(false);
                                setRecipeToFork(null); // Clear the recipe to fork when closing
                            }}
                            className="absolute top-4 right-4 z-10 w-8 h-8 bg-neutral-0 hairline flex items-center justify-center text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-all duration-120 font-mono text-lg p-0 m-0"
                        >
                            ✕
                        </button>

                        {/* Main Content - Ingredients (60%) | Image (40%) */}
                        <div className="flex flex-col min-650-flex-row gap-6 relative overflow-x-hidden" style={{ minHeight: '400px', maxHeight: '500px', paddingRight: '1.5rem' }}>
                            {/* Left Section - Content (60%, Scrollable) */}
                            <div className="flex-[0_0_60%] w-full min-650-w-auto overflow-y-auto overflow-x-hidden no-scrollbar pr-2 relative" style={{ maxHeight: '500px' }}>
                                <div className="p-6 space-y-6">
                                    {/* Recipe Title */}
                                    <div>
                                        <input
                                            type="text"
                                            value={forkRecipeName}
                                            onChange={(e) => setForkRecipeName(e.target.value)}
                                            className="font-mono tracking-tight bg-transparent border-none outline-none focus:bg-neutral-50 px-2 -ml-2 w-full mb-2"
                                            style={{ color: colorTokens.oxfordBlue, fontSize: '2.25rem', lineHeight: '1.2' }}
                                            placeholder="Recipe name"
                                        />
                                        <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                        <div className="flex items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                            <span>{(recipeToFork || recipe).category || 'RECIPE'}</span>
                                            <span>•</span>
                                            <span>BY {(recipeToFork || recipe).author || (recipeToFork || recipe).creator_name || 'UNKNOWN'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Conditional Rendering: Multi-Component vs Simple */}
                                    {forkIsMultiComponent ? (
                                        <>
                                            {/* Multi-Component Mode */}
                                            {forkComponents.map((component, compIndex) => (
                                                <div key={compIndex} className="space-y-4">
                                                    {/* Component Name - Editable */}
                                                    <div>
                                                        <input
                                                            type="text"
                                                            value={component.name || ''}
                                                            onChange={(e) => {
                                                                const newComponents = [...forkComponents];
                                                                newComponents[compIndex] = { ...newComponents[compIndex], name: e.target.value };
                                                                setForkComponents(newComponents);
                                                            }}
                                                            className="font-mono text-sm font-medium bg-transparent border-none outline-none focus:bg-neutral-50 px-2 -ml-2"
                                                            style={{ color: colorTokens.oxfordBlue }}
                                                            placeholder={`Component ${compIndex + 1} name`}
                                                        />
                                                        <span className="font-mono text-sm font-medium" style={{ color: colorTokens.oxfordBlue }}>:</span>
                                                    </div>
                                                    
                                                    {/* Component Prerequisites - Editable */}
                                                    <div>
                                                        <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                                            PREREQUISITES
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(component.prerequisites || []).map((prereq, index) => (
                                                                <div key={index} className="flex items-start">
                                                                    <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">•</span>
                                                                    <textarea
                                                                        value={prereq}
                                                                        onChange={(e) => {
                                                                            const newComponents = [...forkComponents];
                                                                            const newPrereqs = [...(newComponents[compIndex].prerequisites || [])];
                                                                            newPrereqs[index] = e.target.value;
                                                                            newComponents[compIndex] = { ...newComponents[compIndex], prerequisites: newPrereqs };
                                                                            setForkComponents(newComponents);
                                                                        }}
                                                                        placeholder="Enter prerequisite"
                                                                        rows={1}
                                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                                        style={{ height: 'auto', minHeight: '20px' }}
                                                                        onInput={(e) => {
                                                                            e.target.style.height = 'auto';
                                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                                        }}
                                                                    />
                                                                    {(component.prerequisites || []).length > 1 && (
                                                                        <button 
                                                                            onClick={() => {
                                                                                const newComponents = [...forkComponents];
                                                                                const newPrereqs = (newComponents[compIndex].prerequisites || []).filter((_, i) => i !== index);
                                                                                newComponents[compIndex] = { ...newComponents[compIndex], prerequisites: newPrereqs };
                                                                                setForkComponents(newComponents);
                                                                            }}
                                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <button 
                                                            onClick={() => {
                                                                const newComponents = [...forkComponents];
                                                                const newPrereqs = [...(newComponents[compIndex].prerequisites || []), ''];
                                                                newComponents[compIndex] = { ...newComponents[compIndex], prerequisites: newPrereqs };
                                                                setForkComponents(newComponents);
                                                            }}
                                                            className="w-full mt-2 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                                        >
                                                            + ADD PREREQUISITE
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Component Ingredients - Editable */}
                                                    <div>
                                                        <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                                            INGREDIENTS
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(component.ingredients || []).map((ingredient, index) => (
                                                                <div key={index} className="flex items-start">
                                                                    <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">•</span>
                                                                    <textarea
                                                                        value={ingredient}
                                                                        onChange={(e) => {
                                                                            const newComponents = [...forkComponents];
                                                                            const newIngredients = [...(newComponents[compIndex].ingredients || [])];
                                                                            newIngredients[index] = e.target.value;
                                                                            newComponents[compIndex] = { ...newComponents[compIndex], ingredients: newIngredients };
                                                                            setForkComponents(newComponents);
                                                                        }}
                                                                        placeholder="Enter ingredient with quantity"
                                                                        rows={1}
                                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                                        style={{ height: 'auto', minHeight: '20px' }}
                                                                        onInput={(e) => {
                                                                            e.target.style.height = 'auto';
                                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                                        }}
                                                                    />
                                                                    {(component.ingredients || []).length > 1 && (
                                                                        <button 
                                                                            onClick={() => {
                                                                                const newComponents = [...forkComponents];
                                                                                const newIngredients = (newComponents[compIndex].ingredients || []).filter((_, i) => i !== index);
                                                                                newComponents[compIndex] = { ...newComponents[compIndex], ingredients: newIngredients };
                                                                                setForkComponents(newComponents);
                                                                            }}
                                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <button 
                                                            onClick={() => {
                                                                const newComponents = [...forkComponents];
                                                                const newIngredients = [...(newComponents[compIndex].ingredients || []), ''];
                                                                newComponents[compIndex] = { ...newComponents[compIndex], ingredients: newIngredients };
                                                                setForkComponents(newComponents);
                                                            }}
                                                            className="w-full mt-2 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                                        >
                                                            + ADD INGREDIENT
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Component Steps - Editable */}
                                                    <div>
                                                        <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                                            STEPS
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(component.steps || []).map((step, index) => {
                                                                const stepNumber = (component.steps || []).slice(0, index + 1).length;
                                                                return (
                                                                    <div key={index} className="flex items-start">
                                                                        <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                            {stepNumber}
                                                                        </span>
                                                                        <textarea
                                                                            value={step}
                                                                            onChange={(e) => {
                                                                                const newComponents = [...forkComponents];
                                                                                const newSteps = [...(newComponents[compIndex].steps || [])];
                                                                                newSteps[index] = e.target.value;
                                                                                newComponents[compIndex] = { ...newComponents[compIndex], steps: newSteps };
                                                                                setForkComponents(newComponents);
                                                                            }}
                                                                            placeholder="Enter step instruction"
                                                                            rows={2}
                                                                            className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                                            style={{ height: 'auto', minHeight: '40px' }}
                                                                            onInput={(e) => {
                                                                                e.target.style.height = 'auto';
                                                                                e.target.style.height = e.target.scrollHeight + 'px';
                                                                            }}
                                                                        />
                                                                        {(component.steps || []).length > 1 && (
                                                                            <button 
                                                                                onClick={() => {
                                                                                    const newComponents = [...forkComponents];
                                                                                    const newSteps = (newComponents[compIndex].steps || []).filter((_, i) => i !== index);
                                                                                    newComponents[compIndex] = { ...newComponents[compIndex], steps: newSteps };
                                                                                    setForkComponents(newComponents);
                                                                                }}
                                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                                            >
                                                                                ✕
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <button 
                                                            onClick={() => {
                                                                const newComponents = [...forkComponents];
                                                                const newSteps = [...(newComponents[compIndex].steps || []), ''];
                                                                newComponents[compIndex] = { ...newComponents[compIndex], steps: newSteps };
                                                                setForkComponents(newComponents);
                                                            }}
                                                            className="w-full mt-2 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                                        >
                                                            + ADD STEP
                                                        </button>
                                                    </div>
                                                    
                                                    {compIndex < forkComponents.length - 1 && (
                                                        <div className="border-t my-4" style={{ borderColor: colorTokens.notecardBlue }}></div>
                                                    )}
                                                </div>
                                            ))}
                                            
                                            {/* Assembly Section - Editable */}
                                            <div className="border-t my-4" style={{ borderColor: colorTokens.red }}></div>
                                            
                                            {/* Assembly Prerequisites - Editable */}
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                                    ASSEMBLY PREREQUISITES
                                                </div>
                                                <div className="space-y-2">
                                                    {(forkAssembly.prerequisites || []).map((prereq, index) => (
                                                        <div key={index} className="flex items-start">
                                                            <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">•</span>
                                                            <textarea
                                                                value={prereq}
                                                                onChange={(e) => {
                                                                    const newPrereqs = [...(forkAssembly.prerequisites || [])];
                                                                    newPrereqs[index] = e.target.value;
                                                                    setForkAssembly({ ...forkAssembly, prerequisites: newPrereqs });
                                                                }}
                                                                placeholder="Enter assembly prerequisite"
                                                                rows={1}
                                                                className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                                style={{ height: 'auto', minHeight: '20px' }}
                                                                onInput={(e) => {
                                                                    e.target.style.height = 'auto';
                                                                    e.target.style.height = e.target.scrollHeight + 'px';
                                                                }}
                                                            />
                                                            {(forkAssembly.prerequisites || []).length > 1 && (
                                                                <button 
                                                                    onClick={() => {
                                                                        const newPrereqs = (forkAssembly.prerequisites || []).filter((_, i) => i !== index);
                                                                        setForkAssembly({ ...forkAssembly, prerequisites: newPrereqs });
                                                                    }}
                                                                    className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                                >
                                                                    ✕
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <button 
                                                    onClick={() => {
                                                        const newPrereqs = [...(forkAssembly.prerequisites || []), ''];
                                                        setForkAssembly({ ...forkAssembly, prerequisites: newPrereqs });
                                                    }}
                                                    className="w-full mt-2 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                                >
                                                    + ADD PREREQUISITE
                                                </button>
                                            </div>
                                            
                                            {/* Assembly Steps - Editable */}
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                                    ASSEMBLY STEPS
                                                </div>
                                                <div className="space-y-2">
                                                    {(forkAssembly.steps || []).map((step, index) => {
                                                        const stepNumber = (forkAssembly.steps || []).slice(0, index + 1).length;
                                                        return (
                                                            <div key={index} className="flex items-start">
                                                                <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                    {stepNumber}
                                                                </span>
                                                                <textarea
                                                                    value={step}
                                                                    onChange={(e) => {
                                                                        const newSteps = [...(forkAssembly.steps || [])];
                                                                        newSteps[index] = e.target.value;
                                                                        setForkAssembly({ ...forkAssembly, steps: newSteps });
                                                                    }}
                                                                    placeholder="Enter assembly step"
                                                                    rows={2}
                                                                    className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                                    style={{ height: 'auto', minHeight: '40px' }}
                                                                    onInput={(e) => {
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                                    }}
                                                                />
                                                                {(forkAssembly.steps || []).length > 1 && (
                                                                    <button 
                                                                        onClick={() => {
                                                                            const newSteps = (forkAssembly.steps || []).filter((_, i) => i !== index);
                                                                            setForkAssembly({ ...forkAssembly, steps: newSteps });
                                                                        }}
                                                                        className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <button 
                                                    onClick={() => {
                                                        const newSteps = [...(forkAssembly.steps || []), ''];
                                                        setForkAssembly({ ...forkAssembly, steps: newSteps });
                                                    }}
                                                    className="w-full mt-2 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                                >
                                                    + ADD STEP
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* Simple Recipe Mode */}
                                    {/* Prerequisites Section */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                            PREREQUISITES
                                        </div>
                                        <div className="space-y-3">
                                            {forkInstructions.prerequisites.map((prerequisite, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <textarea
                                                        value={prerequisite}
                                                        onChange={(e) => {
                                                            const newPrereqs = [...forkInstructions.prerequisites];
                                                            newPrereqs[index] = e.target.value;
                                                            setForkInstructions({ ...forkInstructions, prerequisites: newPrereqs });
                                                        }}
                                                        placeholder="Enter prerequisite (e.g., Preheat oven to 350°F)"
                                                        rows={1}
                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                        style={{ 
                                                            height: 'auto',
                                                            minHeight: '20px'
                                                        }}
                                                        onInput={(e) => {
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                    />
                                                    {forkInstructions.prerequisites.length > 1 && (
                                                        <button 
                                                            onClick={() => setForkInstructions({ 
                                                                ...forkInstructions, 
                                                                prerequisites: forkInstructions.prerequisites.filter((_, i) => i !== index)
                                                            })}
                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button 
                                            onClick={() => setForkInstructions({ 
                                                ...forkInstructions, 
                                                prerequisites: [...forkInstructions.prerequisites, '']
                                            })} 
                                            className="w-full mt-4 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                        >
                                            + ADD PREREQUISITE
                                        </button>
                                    </div>

                                    {/* Ingredients Section */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                            INGREDIENTS
                                        </div>
                                        <div className="space-y-3">
                                            {forkIngredients.map((ingredient, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <textarea
                                                        value={ingredient}
                                                        onChange={(e) => {
                                                            const newArr = [...forkIngredients];
                                                            newArr[index] = e.target.value;
                                                            setForkIngredients(newArr);
                                                        }}
                                                        placeholder="Enter ingredient with quantity (e.g., 2 cups flour)"
                                                        rows={1}
                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                        style={{ 
                                                            height: 'auto',
                                                            minHeight: '20px'
                                                        }}
                                                        onInput={(e) => {
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                        data-walkthrough={index === 0 ? "fork-ingredient-0" : undefined}
                                                    />
                                                    {forkIngredients.length > 1 && (
                                                        <button 
                                                            onClick={() => setForkIngredients(forkIngredients.filter((_, i) => i !== index))}
                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button 
                                            onClick={() => setForkIngredients([...forkIngredients, ''])} 
                                            className="w-full mt-4 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                        >
                                            + ADD INGREDIENT
                                        </button>
                                    </div>

                                    {/* Steps Section */}
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                            STEPS
                                        </div>
                                        <div className="space-y-3">
                                            {forkInstructions.steps.map((step, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <textarea
                                                        value={step}
                                                        onChange={(e) => {
                                                            const newSteps = [...forkInstructions.steps];
                                                            newSteps[index] = e.target.value;
                                                            setForkInstructions({ ...forkInstructions, steps: newSteps });
                                                        }}
                                                        placeholder="Enter step instruction"
                                                        rows={2}
                                                        className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                        style={{ 
                                                            height: 'auto',
                                                            minHeight: '40px'
                                                        }}
                                                        onInput={(e) => {
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = e.target.scrollHeight + 'px';
                                                        }}
                                                    />
                                                    {forkInstructions.steps.length > 1 && (
                                                        <button 
                                                            onClick={() => setForkInstructions({ 
                                                                ...forkInstructions, 
                                                                steps: forkInstructions.steps.filter((_, i) => i !== index)
                                                            })}
                                                            className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button 
                                            onClick={() => setForkInstructions({ 
                                                ...forkInstructions, 
                                                steps: [...forkInstructions.steps, '']
                                            })} 
                                            className="w-full mt-4 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                        >
                                            + ADD STEP
                                        </button>
                                    </div>
                                        </>
                                    )}
                                </div>
                                {/* Fade effect overlay - sticky to bottom, indicates more content below */}
                                <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-neutral-0 to-transparent pointer-events-none z-10"></div>
                            </div>

                            {/* Right Section - Image (40%, Top Right on desktop >650px, Bottom on mobile) */}
                            {/* CRITICAL: Use recipeToFork images if available, otherwise use recipe images */}
                            <div className="flex-[0_0_40%] w-full min-650-w-auto flex-shrink-0 overflow-hidden order-2 min-650-order-1">
                                <div className="w-full pt-6 pr-6 pb-6 min-650-pb-0">
                                    <div className="relative w-full overflow-hidden hairline min-650-hidden" style={{ height: '200px', width: '100%' }}>
                                        <SignedImage
                                            src={(recipeToFork?.images || recipe.images) && (recipeToFork?.images || recipe.images).length > 0 ? (recipeToFork?.images || recipe.images)[0] : null}
                                            alt={`${(recipeToFork || recipe).name || (recipeToFork || recipe).title}`}
                                            className="w-full h-full object-cover"
                                            category={(recipeToFork || recipe).category}
                                        />
                                    </div>
                                    <div className="hidden min-650-block relative w-full overflow-hidden hairline" style={{ height: 'calc(500px - 3rem)', width: '100%' }}>
                                        <SignedImage
                                            src={(recipeToFork?.images || recipe.images) && (recipeToFork?.images || recipe.images).length > 0 ? (recipeToFork?.images || recipe.images)[0] : null}
                                            alt={`${(recipeToFork || recipe).name || (recipeToFork || recipe).title}`}
                                            className="w-full h-full object-cover"
                                            category={(recipeToFork || recipe).category}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Bar - Full Width with Create Fork Button */}
                        <div className="p-6" style={{ borderTop: '1px solid #0D0D0D', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}>
                            <button 
                                onClick={handleCreateFork} 
                                disabled={forking || !forkRecipeName.trim()} 
                                className="w-full px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all border-none text-white hover:opacity-60 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: colorTokens.oxfordBlue }}
                                data-walkthrough="fork-submit"
                            >
                                {forking ? 'CREATING FORK...' : 'CREATE FORK'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )}
        
        <div className={pageContainerClass}>
            <div className={innerContainerClass}>
                {showLineageModal ? (
                    /* Lineage Viewer - Full Page Replacement */
                    <div className="w-full h-[calc(100vh-2rem)] -my-8 relative" style={{ zIndex: 1, isolation: 'isolate' }}>
                        <LineageViewerV2 
                            recipeId={recipe?.id}
                            currentRecipeId={selectedLineageRecipe?.id || id}
                            onRecipeSelect={(selectedRecipe) => {
                                console.log('🔀 Recipe selected in lineage:', { 
                                    selectedId: selectedRecipe.id, 
                                    selectedName: selectedRecipe.name,
                                    currentSelectedId: selectedLineageRecipe?.id,
                                    currentRecipeId: recipe?.id
                                });
                                
                                // If clicking the already selected recipe, navigate to it
                                if (selectedLineageRecipe && selectedLineageRecipe.id === selectedRecipe.id) {
                                navigate(`/recipes/${selectedRecipe.id}`);
                                    setSelectedLineageRecipe(null);
                                    setRecipeToFork(null); // Clear fork target when navigating
                                } else {
                                    // Just select the recipe, don't navigate
                                    setSelectedLineageRecipe(selectedRecipe);
                                    // Don't clear recipeToFork here - it should persist until fork is created or modal closes
                                }
                            }}
                            showCompareSelectionModal={showCompareSelectionModal}
                            onToggleCompareRecipe={toggleRecipeSelection}
                            compareMode={lineageCompareMode}
                            onCompareModeChange={setLineageCompareMode}
                            refreshTrigger={lineageRefreshKey}
                        />
                    </div>
                ) : (
                    <>
                {/* Recipe Card - Unified Layout */}
                <div className={cardWrapperClass}>
                    <div className={cardInnerWrapperClass}>
                    {!recipe.is_original && (
                        <>
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 translate-x-2 translate-y-2 sm:translate-x-4 sm:translate-y-4 bg-neutral-0 hairline pointer-events-none group-hover:shadow-popup transition-all duration-200"
                            />
                            <div
                                aria-hidden="true"
                                className="absolute inset-0 translate-x-1 translate-y-1 sm:translate-x-2 sm:translate-y-2 bg-neutral-0 hairline pointer-events-none"
                            />
                        </>
                    )}
                    <div className={cardClass}>
                        {/* Recipe Card Overlay Button - Top Right */}
                        <button
                            onClick={() => setShowRecipeCardOverlay(true)}
                            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center font-mono text-lg transition-all border-none hover:opacity-60"
                            style={{ backgroundColor: colorTokens.oxfordBlue, color: '#FFFFFF' }}
                            aria-label="Expand recipe card"
                        >
                            ⤢
                        </button>
                        
                        {/* Three-Dot Menu Button - Below Full Screen Button */}
                        {user && (
                            <div className="absolute top-16 right-4 z-20 menu-dropdown-container">
                                <button
                                    onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                                    className="w-10 h-10 flex items-center justify-center font-mono text-lg transition-all border-none bg-neutral-0 hairline text-[#1b263b] hover:bg-neutral-900 hover:text-neutral-0"
                                    aria-label="Recipe menu"
                                >
                                    ⋯
                                </button>
                                
                                {/* Dropdown Menu */}
                                {showMenuDropdown && recipe && (
                                    <div className="absolute top-full right-0 mt-2 bg-neutral-0 hairline min-w-[200px] z-30">
                                        {isOwner && (
                                            <>
                                                <Link
                                                    to={`/edit/${recipe.id}`}
                                                    onClick={() => setShowMenuDropdown(false)}
                                                    className="block px-4 py-3 text-left font-mono text-sm uppercase tracking-wider text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-colors border-none"
                                                >
                                                    EDIT
                                                </Link>
                                                <button
                                                    onClick={() => {
                                                        setShowMenuDropdown(false);
                                                        setDeleteModalOpen(true);
                                                    }}
                                                    className="w-full px-4 py-3 text-left font-mono text-sm uppercase tracking-wider text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-colors border-none"
                                                >
                                                    DELETE
                                                </button>
                                            </>
                                        )}
                                        {recipe.collection_id ? (
                                            <button
                                                onClick={() => {
                                                    setShowMenuDropdown(false);
                                                    removeFromCollection();
                                                }}
                                                className="w-full px-4 py-3 text-left font-mono text-sm uppercase tracking-wider text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-colors border-none"
                                            >
                                                REMOVE FROM COLLECTION
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setShowMenuDropdown(false);
                                                    setShowAddToCollection(true);
                                                }}
                                                className="w-full px-4 py-3 text-left font-mono text-sm uppercase tracking-wider text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-colors border-none"
                                            >
                                                ADD TO COLLECTION
                                            </button>
                                        )}
                                        <button
                                            onClick={handleAddToGroceryList}
                                            disabled={addingToGroceryList}
                                            className="w-full px-4 py-3 text-left font-mono text-sm uppercase tracking-wider text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-colors border-none flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {addingToGroceryList ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span>ADDING...</span>
                                                </>
                                            ) : (
                                                <>
                                                    ADD TO GROCERY LIST
                                                    {subscription && !['unlimited', 'beta', 'admin', 'family'].includes(subscription.planType) && (
                                                        <span className="text-xs">👑</span>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Mobile Layout - Stacked: Title, Image, Content */}
                        <div className="lg:hidden space-y-6">
                            {/* Title and Basic Info Section */}
                            <div className="mb-4">
                                {/* Recipe Title - Typewriter font, bottom left aligned */}
                                <div className="text-left mb-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h1 className="text-4xl font-mono tracking-tight" style={{ color: colorTokens.oxfordBlue }}>
                                            {recipe.name || recipe.title}
                                        </h1>
                                    </div>
                                </div>
                                
                                {/* Red dividing line */}
                                <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                
                                {/* Author info - centered */}
                                <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                    <span>{recipe.category || 'RECIPE'}</span>
                                    <span>•</span>
                                    {recipe.is_transfer ? (
                                        <>
                                            <button
                                                onClick={() => setShowAttributionPopup(true)}
                                                className="cursor-pointer transition-colors border-none p-0"
                                                style={{ 
                                                    color: 'inherit',
                                                    backgroundColor: 'transparent',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.color = colorTokens.richBlack;
                                                    e.target.style.backgroundColor = 'transparent';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.color = 'inherit';
                                                    e.target.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                BY {recipe.author || recipe.creator_name || 'UNKNOWN'}
                                            </button>
                                            <span>•</span>
                                            <button
                                                onClick={() => setShowAttributionPopup(true)}
                                                className="cursor-pointer transition-colors border-none p-0"
                                                style={{ 
                                                    color: colorTokens.notecardBlue,
                                                    backgroundColor: 'transparent',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.color = colorTokens.richBlack;
                                                    e.target.style.backgroundColor = 'transparent';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.color = colorTokens.notecardBlue;
                                                    e.target.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                TRANSFERRED BY {recipe.creator_name}
                                            </button>
                                        </>
                                    ) : (
                                        <span>BY {recipe.author || recipe.creator_name || 'UNKNOWN'}</span>
                                    )}
                                    {!recipe.is_original && !recipe.is_transfer && !showCompareModal && (
                                        <>
                                            <span>•</span>
                                            <span style={{ color: colorTokens.notecardBlue }}>FORKED BY {recipe.forker_name}</span>
                                        </>
                                    )}
                                    {lineage && lineage.recipes && lineage.recipes.length > 1 && (
                                        <>
                                            <span>•</span>
                                            <span className="text-neutral-900 opacity-40">
                                                {getCurrentRecipeIndex() + 1} of {lineage.recipes.length}
                                            </span>
                                        </>
                                    )}
                                </div>
                                
                            </div>

                            {/* Image Section for Mobile */}
                            <div className="h-[40vh] overflow-hidden">
                                <div 
                                    className={`relative w-full h-full overflow-hidden hairline group ${
                                        imageDragOver === currentImageIndex ? 'ring-2 ring-neutral-900' : ''
                                    }`}
                                    onDrop={(e) => isOwner && handleImageDrop(e, currentImageIndex)}
                                    onDragOver={(e) => isOwner && handleImageDragOver(e, currentImageIndex)}
                                    onDragLeave={handleImageDragLeave}
                                >
                                    <SignedImage
                                        src={recipe.images && recipe.images.length > 0 ? recipe.images[currentImageIndex] : null}
                                        alt={`${recipe.name || recipe.title} - Image ${currentImageIndex + 1}`}
                                        className="w-full h-full object-cover"
                                        category={recipe.category}
                                    />
                                    
                                    {/* Edit Button - Show on hover for owners */}
                                    {isOwner && (
                                        <button
                                            onClick={() => handleImageFileSelect(currentImageIndex)}
                                            disabled={updatingImage}
                                            className="absolute bottom-2 left-2 w-8 h-8 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100 z-10"
                                            title="Replace image"
                                        >
                                            ✎
                                        </button>
                                    )}

                                    {/* Navigation Arrows - Only show if multiple images */}
                                    {recipe.images && recipe.images.length > 1 && (
                                        <>
                                            <button
                                                onClick={prevImage}
                                                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-neutral-0 text-neutral-900 hover:bg-neutral-50 transition-all duration-200 flex items-center justify-center hairline"
                                                aria-label="Previous image"
                                            >
                                                ←
                                            </button>
                                            <button
                                                onClick={nextImage}
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-neutral-0 text-neutral-900 hover:bg-neutral-50 transition-all duration-200 flex items-center justify-center hairline"
                                                aria-label="Next image"
                                            >
                                                →
                                            </button>
                                        </>
                                    )}

                                    {/* Image Counter */}
                                    {recipe.images && recipe.images.length > 1 && (
                                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs font-mono">
                                            {currentImageIndex + 1} / {recipe.images.length}
                                        </div>
                                    )}
                                </div>

                                {/* Image Indicators/Thumbnails - Only show if multiple images */}
                                {recipe.images && recipe.images.length > 1 && (
                                    <div className="flex justify-center mt-4 space-x-2">
                                        {recipe.images.map((image, index) => (
                                            <div
                                                key={index}
                                                className={`relative w-12 h-12 overflow-hidden hairline transition-opacity duration-200 group ${
                                                    index === currentImageIndex ? 'opacity-100 ring-2 ring-neutral-900' : 'opacity-60 hover:opacity-80'
                                                } ${
                                                    imageDragOver === index ? 'ring-2 ring-neutral-900' : ''
                                                }`}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (isOwner) {
                                                        handleImageDrop(e, index);
                                                    }
                                                }}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (isOwner) {
                                                        handleImageDragOver(e, index);
                                                    }
                                                }}
                                                onDragLeave={handleImageDragLeave}
                                            >
                                                <button
                                                    onClick={() => goToImage(index)}
                                                    className="w-full h-full"
                                                >
                                                    <SignedImage
                                                        src={image}
                                                        alt={`${recipe.name || recipe.title} - Thumbnail ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                        category={recipe.category}
                                                    />
                                                </button>
                                                {/* Edit Button - Show on hover for owners */}
                                                {isOwner && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleImageFileSelect(index);
                                                        }}
                                                        disabled={updatingImage}
                                                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 z-10"
                                                        title="Replace image"
                                                    >
                                                        <span className="text-white text-lg">✎</span>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Content Section for Mobile */}
                            <div className="space-y-6">

                                    {/* Recipe Information */}
                                    <div className="space-y-4">
                                        {/* Quick Info */}
                                        <div>
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">TIME</div>
                                            {(() => {
                                                // Only show session data to recipe owners
                                                if (isOwner) {
                                                    // Priority 1: Use actual duration from database session data
                                                    if (sessionData?.actual_duration_seconds) {
                                                        const minutes = Math.floor(sessionData.actual_duration_seconds / 60);
                                                        const seconds = (sessionData.actual_duration_seconds % 60).toString().padStart(2, '0');
                                                        return (
                                                            <div className="font-mono text-lg">
                                                                {minutes}:{seconds}
                                                                <div className="text-xs text-neutral-900 opacity-60">actual</div>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    // Priority 2: Use reflection data if available
                                                    try {
                                                        const reflection = recipe.reflection ? JSON.parse(recipe.reflection) : null;
                                                        if (reflection?.sessionData?.totalElapsedTime) {
                                                            const minutes = Math.floor(reflection.sessionData.totalElapsedTime / 60);
                                                            const seconds = (reflection.sessionData.totalElapsedTime % 60).toString().padStart(2, '0');
                                                            return (
                                                                <div className="font-mono text-lg">
                                                                    {minutes}:{seconds}
                                                                    <div className="text-xs text-neutral-900 opacity-60">actual</div>
                                                                </div>
                                                            );
                                                        }
                                                    } catch (e) {
                                                        console.error('Error parsing reflection data:', e);
                                                    }
                                                }
                                                
                                                // Fallback to expected time for non-owners or when no session data
                                                return <div className="font-mono text-lg">{formatCookingTime(recipe.cooking_time)}</div>;
                                            })()}
                                        </div>
                                        <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">CATEGORY</div>
                                            <div className="font-mono text-lg">{recipe.category || 'Uncategorized'}</div>
                                        </div>
                                        <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">CREATED</div>
                                            <div className="font-mono text-lg">{new Date(recipe.created_at).toLocaleDateString()}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                {recipe.description && (
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">DESCRIPTION</div>
                                        <Text className="text-sm leading-relaxed italic text-neutral-900 opacity-80">
                                            {recipe.description}
                                        </Text>
                                    </div>
                                )}

                                {/* Section Separator */}
                                <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                {/* Check for Multi-Component Recipe */}
                                {(() => {
                                    const combinedData = combineMultiComponentData(recipe);
                                    if (combinedData) {
                                        return (
                                            <>
                                                {/* Ingredients with Component Headers */}
                                                <div>
                                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INGREDIENTS</div>
                                                    {combinedData.ingredients.length > 0 ? (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            {combinedData.ingredients.map((item, index) => {
                                                                if (item.type === 'header') {
                                                                    return (
                                                                        <div key={index} className="col-span-full mt-3 first:mt-0">
                                                                            <Text className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                                {item.text}
                                                                            </Text>
                                                                        </div>
                                                                    );
                                                                }
                                                                return (
                                                                    <div key={index} className="flex items-start">
                                                                        <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">•</span>
                                                                        <Text className="flex-1 text-sm">{item.text}</Text>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <Text className="text-neutral-900 opacity-60 text-sm">No ingredients listed.</Text>
                                                    )}
                                                </div>

                                                {/* Section Separator */}
                                                <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                                {/* Prerequisites with Component Headers */}
                                                {combinedData.prerequisites.length > 0 && (
                                                    <div>
                                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES</div>
                                                        <div className="space-y-2">
                                                            {combinedData.prerequisites.map((item, index) => {
                                                                if (item.type === 'header') {
                                                                    return (
                                                                        <div key={index} className="mt-3 first:mt-0">
                                                                            <Text className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                                {item.text}
                                                                            </Text>
                                                                        </div>
                                                                    );
                                                                }
                                                                return (
                                                                    <div key={index} className="flex items-start">
                                                                        <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">•</span>
                                                                        <Text className="flex-1 text-sm">{item.text}</Text>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Section Separator */}
                                                <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                                {/* Steps with Component Headers */}
                                                <div>
                                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INSTRUCTIONS</div>
                                                    {combinedData.steps.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {combinedData.steps.map((item, index) => {
                                                                if (item.type === 'header') {
                                                                    return (
                                                                        <div key={index} className="mt-4 first:mt-0">
                                                                            <Text className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                                {item.text}
                                                                            </Text>
                                                                        </div>
                                                                    );
                                                                }
                                                                // Count only item steps for numbering
                                                                const stepNumber = combinedData.steps.slice(0, index + 1).filter(s => s.type === 'item').length;
                                                                
                                                                // Get timing segments for this step from steps_with_timing
                                                                const stepWithTiming = recipe.steps_with_timing && Array.isArray(recipe.steps_with_timing) 
                                                                    ? recipe.steps_with_timing[stepNumber - 1] 
                                                                    : null;
                                                                const timingSegments = stepWithTiming?.timing_segments || [];
                                                                
                                                                // Check if this step has any timers
                                                                const hasTimers = timingSegments.some(s => s.expectedSeconds > 0);
                                                                
                                                                return (
                                                                    <div key={index} className="flex items-start relative">
                                                                        <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                            {stepNumber}
                                                                        </span>
                                                                        <div 
                                                                            className="flex-1 text-sm relative"
                                                                        >
                                                                            <div className="flex items-start gap-2">
                                                                                <Text as="span" className="flex-1">{item.text}</Text>
                                                                                {hasTimers && (
                                                                                    <span 
                                                                                        className="text-xs font-mono flex-shrink-0 relative z-10"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            if (openTimerPopover?.stepIndex === stepNumber - 1 && openTimerPopover?.section === 'multi') {
                                                                                                setOpenTimerPopover(null);
                                                                                            } else {
                                                                                                setOpenTimerPopover({ stepIndex: stepNumber - 1, stepText: item.text, section: 'multi' });
                                                                                            }
                                                                                        }}
                                                                                        style={{ cursor: 'pointer', color: '#0D0D0D', opacity: 0.4 }}
                                                                                    >
                                                                                        ⏱
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {hasTimers && openTimerPopover?.stepIndex === stepNumber - 1 && openTimerPopover?.section === 'multi' && (
                                                                                <StepTimerPopover
                                                                                    timingSegments={timingSegments}
                                                                                    stepText={item.text}
                                                                                    stepNumber={stepNumber}
                                                                                    onClose={() => setOpenTimerPopover(null)}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <Text className="text-neutral-900 opacity-60 text-sm">No instructions provided.</Text>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    }
                                    return false; // Return false to trigger the else branch
                                })() || (
                                    <>
                                        {/* Simple Recipe Display - Original UI */}
                                {/* Ingredients */}
                                <div>
                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INGREDIENTS</div>
                                    {recipe.ingredients && recipe.ingredients.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {recipe.ingredients.map((ingredient, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <Text className="flex-1 text-sm">
                                                        {typeof ingredient === 'string' ? ingredient : ingredient.name || 'Invalid ingredient'}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <Text className="text-neutral-900 opacity-60 text-sm">No ingredients listed.</Text>
                                    )}
                                </div>

                                {/* Section Separator */}
                                <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                {/* Prerequisites */}
                                {recipe.instructions && typeof recipe.instructions === 'object' && recipe.instructions.prerequisites && recipe.instructions.prerequisites.length > 0 && (
                                    <div>
                                        <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">PREREQUISITES</div>
                                        <div className="space-y-2">
                                            {recipe.instructions.prerequisites.map((prerequisite, index) => (
                                                <div key={index} className="flex items-start">
                                                    <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">
                                                        {index + 1}.
                                                    </span>
                                                    <Text className="flex-1 text-sm">{prerequisite}</Text>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Section Separator */}
                                <div className="border-t" style={{ borderColor: colorTokens.notecardBlue }}></div>

                                {/* Instructions */}
                                <div>
                                    <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">INSTRUCTIONS</div>
                                    
                                    {/* Show live session instructions if available */}
                                    {(() => {
                                        try {
                                            const reflection = recipe.reflection ? JSON.parse(recipe.reflection) : null;
                                            if (reflection?.sessionData?.sessionInstructions?.length > 0) {
                                                return (
                                                    <div className="mb-6">
                                                        <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">LIVE SESSION STEPS</div>
                                                        <div className="space-y-3">
                                                            {reflection.sessionData.sessionInstructions.map((step, index) => (
                                                                <div key={index} className="flex items-start">
                                                                    <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                        {index + 1}
                                                                    </span>
                                                                    <div className="flex-1">
                                                                        <Text className="font-semibold mb-1 text-sm">{step.title}</Text>
                                                                        <Text className="leading-relaxed text-neutral-900 opacity-80 text-sm">{step.body}</Text>
                                                                        {step.durationSec && (
                                                                            <Text className="text-xs text-neutral-900 opacity-60 mt-1">
                                                                                {Math.floor(step.durationSec / 60)}:{(step.durationSec % 60).toString().padStart(2, '0')} min
                                                                            </Text>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        } catch (e) {
                                            console.error('Error parsing live session instructions:', e);
                                        }
                                        return null;
                                    })()}
                                    
                                    {recipe.instructions && typeof recipe.instructions === 'object' ? (
                                        <div>
                                            {/* Cooking Steps */}
                                            {recipe.instructions.steps && recipe.instructions.steps.length > 0 && (
                                                <div className="space-y-3">
                                                    {recipe.instructions.steps.map((step, index) => {
                                                        // Get timing segments for this step from steps_with_timing
                                                        const stepWithTiming = recipe.steps_with_timing && Array.isArray(recipe.steps_with_timing) 
                                                            ? recipe.steps_with_timing[index] 
                                                            : null;
                                                        const timingSegments = stepWithTiming?.timing_segments || [];
                                                        
                                                        // Check if this step has any timers
                                                        const hasTimers = timingSegments.some(s => s.expectedSeconds > 0);
                                                        
                                                        return (
                                                            <div 
                                                                key={index} 
                                                                className="flex items-start relative"
                                                            >
                                                                <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                    {index + 1}
                                                                </span>
                                                                <div 
                                                                    className="flex-1 leading-relaxed text-sm relative"
                                                                >
                                                                    <div className="flex items-start gap-2">
                                                                        <Text as="span" className="flex-1">{step}</Text>
                                                                        {hasTimers && (
                                                                            <span 
                                                                                className="text-xs font-mono flex-shrink-0 relative z-10"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    if (openTimerPopover?.stepIndex === index && openTimerPopover?.section === 'mobile') {
                                                                                        setOpenTimerPopover(null);
                                                                                    } else {
                                                                                        setOpenTimerPopover({ stepIndex: index, stepText: step, section: 'mobile' });
                                                                                    }
                                                                                }}
                                                                                style={{ cursor: 'pointer', color: '#0D0D0D', opacity: 0.4 }}
                                                                            >
                                                                                ⏱
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {hasTimers && openTimerPopover?.stepIndex === index && openTimerPopover?.section === 'mobile' && (
                                                                        <StepTimerPopover
                                                                            timingSegments={timingSegments}
                                                                            stepText={step}
                                                                            stepNumber={index + 1}
                                                                            onClose={() => setOpenTimerPopover(null)}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <Text className="text-neutral-900 opacity-60 text-sm">No instructions provided.</Text>
                                    )}
                                    </div>
                                    </>
                                )}
                                {/* End of multi-component / simple recipe conditional */}

                                    {/* Propose Merge Button - Only show for forks where user is the owner */}
                                    {!recipe.is_original && recipe.owner_id === user?.uid && (
                                        <div className="mt-6 p-4 bg-neutral-50 hairline">
                                            <div className="text-center">
                                                <Text className="text-sm font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-3">
                                                    SUGGEST CHANGES
                                                </Text>
                                                <button
                                                    onClick={handleProposeMerge}
                                                    className="px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all border-none text-white hover:opacity-60"
                                                    style={{ backgroundColor: colorTokens.oxfordBlue }}
                                                >
                                                    {isOnlyContributor() ? 'UPDATE GOLD STANDARD' : 'PROPOSE GOLD STANDARD CHANGE'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Success message for merge proposal */}
                                    {mergeProposalSuccess && (
                                        <div className="mt-4 p-4 bg-green-50 border border-green-200">
                                        <Text className="text-green-900 text-sm">
                                            {isOnlyContributor() ? '✅ Gold Standard updated successfully!' : '✅ Gold Standard change proposal submitted successfully! The recipe owner will be notified.'}
                                        </Text>
                                        </div>
                                    )}

                                    {/* Fade effect overlay - sticky to bottom, indicates more content below */}
                                    <div className="sticky bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-neutral-0 to-transparent pointer-events-none z-10"></div>
                                </div>
                            </div>
                        </div>

                        {/* Desktop Layout - Side by side: Content (60%) | Image (40%) */}
                        <div className="hidden lg:flex lg:gap-6 relative" style={{ maxHeight: 'calc(80vh - 4rem)' }}>
                            {/* Left Section - Text Content (60%) - Scrollable */}
                            <div className="flex-[0_0_60%] overflow-y-auto no-scrollbar pr-2 relative" style={{ maxHeight: 'calc(85vh - 8rem)' }}>
                                <div className="space-y-6">
                                    {/* Recipe Header - Notecard Style */}
                                    <div className="mb-4">
                                        {/* Recipe Title - Typewriter font, bottom left aligned */}
                                        <div className="text-left mb-3">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h1 className="text-4xl font-mono tracking-tight" style={{ color: colorTokens.oxfordBlue }}>
                                                    {recipe.name || recipe.title}
                                                </h1>
                                            </div>
                                        </div>
                                        
                                        {/* Red dividing line */}
                                        <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                        
                                        {/* Author info - centered */}
                                        <div className="flex justify-center items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                            <span>{recipe.category || 'RECIPE'}</span>
                                            <span>•</span>
                                            {recipe.is_transfer ? (
                                                <>
                                                    <button
                                                        onClick={() => setShowAttributionPopup(true)}
                                                        className="cursor-pointer transition-colors border-none p-0"
                                                        style={{ 
                                                            color: 'inherit',
                                                            backgroundColor: 'transparent',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.color = colorTokens.richBlack;
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.color = 'inherit';
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        BY {recipe.author || recipe.creator_name || 'UNKNOWN'}
                                                    </button>
                                                    <span>•</span>
                                                    <button
                                                        onClick={() => setShowAttributionPopup(true)}
                                                        className="cursor-pointer transition-colors border-none p-0"
                                                        style={{ 
                                                            color: colorTokens.notecardBlue,
                                                            backgroundColor: 'transparent',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.color = colorTokens.richBlack;
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.color = colorTokens.notecardBlue;
                                                            e.target.style.backgroundColor = 'transparent';
                                                        }}
                                                    >
                                                        TRANSFERRED BY {recipe.creator_name}
                                                    </button>
                                                </>
                                            ) : (
                                                <span>BY {recipe.author || recipe.creator_name || 'UNKNOWN'}</span>
                                            )}
                                            {!recipe.is_original && !recipe.is_transfer && !showCompareModal && (
                                                <>
                                                    <span>•</span>
                                                    <span style={{ color: colorTokens.notecardBlue }}>FORKED BY {recipe.forker_name}</span>
                                                </>
                                            )}
                                            {lineage && lineage.recipes && lineage.recipes.length > 1 && (
                                                <>
                                                    <span>•</span>
                                                    <span className="text-neutral-900 opacity-40">
                                                        {getCurrentRecipeIndex() + 1} of {lineage.recipes.length}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        
                                        {/* Source URL for transfers */}
                                        {recipe.is_transfer && recipe.source_url && (
                                            <div className="mt-1 text-center">
                                                <a 
                                                    href={recipe.source_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-xs font-mono text-neutral-900 opacity-40 hover:opacity-60 transition-opacity underline"
                                                >
                                                    SOURCE: {new URL(recipe.source_url).hostname}
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {/* Recipe Information */}
                                    <div className="space-y-4">
                                        {/* Quick Info */}
                                        <div>
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">TIME</div>
                                            {(() => {
                                                // Only show session data to recipe owners
                                                if (isOwner) {
                                                    // Priority 1: Use actual duration from database session data
                                                    if (sessionData?.actual_duration_seconds) {
                                                        const minutes = Math.floor(sessionData.actual_duration_seconds / 60);
                                                        const seconds = (sessionData.actual_duration_seconds % 60).toString().padStart(2, '0');
                                                        return (
                                                            <div className="font-mono text-lg">
                                                                {minutes}:{seconds}
                                                                <div className="text-xs text-neutral-900 opacity-60">actual</div>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    // Priority 2: Use reflection data if available
                                                    try {
                                                        const reflection = recipe.reflection ? JSON.parse(recipe.reflection) : null;
                                                        if (reflection?.sessionData?.totalElapsedTime) {
                                                            const minutes = Math.floor(reflection.sessionData.totalElapsedTime / 60);
                                                            const seconds = (reflection.sessionData.totalElapsedTime % 60).toString().padStart(2, '0');
                                                            return (
                                                                <div className="font-mono text-lg">
                                                                    {minutes}:{seconds}
                                                                    <div className="text-xs text-neutral-900 opacity-60">actual</div>
                                                                </div>
                                                            );
                                                        }
                                                    } catch (e) {
                                                        console.error('Error parsing reflection data:', e);
                                                    }
                                                }
                                                
                                                // Fallback to recipe cooking time for non-owners or when no session data
                                                if (recipe.cooking_time && recipe.cooking_time > 0) {
                                                    const hours = Math.floor(recipe.cooking_time / 60);
                                                    const minutes = recipe.cooking_time % 60;
                                                    if (hours > 0) {
                                                        return (
                                                            <div className="font-mono text-lg">
                                                                {hours}h {minutes}m
                                                                <div className="text-xs text-neutral-900 opacity-60">estimated</div>
                                                            </div>
                                                        );
                                                    } else {
                                                        return (
                                                            <div className="font-mono text-lg">
                                                                {minutes}m
                                                                <div className="text-xs text-neutral-900 opacity-60">estimated</div>
                                                            </div>
                                                        );
                                                    }
                                                }
                                                
                                                return <div className="font-mono text-lg text-neutral-900 opacity-40">—</div>;
                                            })()}
                                            </div>
                                            <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">SERVES</div>
                                            <div className="font-mono text-lg text-neutral-900 opacity-40">—</div>
                                            </div>
                                            <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-1">DIFFICULTY</div>
                                            <div className="font-mono text-lg text-neutral-900 opacity-40">—</div>
                                            </div>
                                        </div>
                                        </div>

                                        {/* Check for Multi-Component Recipe (Desktop) */}
                                        {(() => {
                                            const combinedData = combineMultiComponentData(recipe);
                                            if (combinedData) {
                                                return (
                                                    <>
                                                        {/* Ingredients with Component Headers (Desktop) */}
                                                        <div>
                                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">INGREDIENTS</div>
                                                            {combinedData.ingredients.length > 0 ? (
                                                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-4 gap-y-2">
                                                                    {combinedData.ingredients.map((item, index) => {
                                                                        if (item.type === 'header') {
                                                                            return (
                                                                                <div key={index} className="col-span-full mt-3 first:mt-0">
                                                                                    <Text className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                                        {item.text}
                                                                                    </Text>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <div key={index} className="flex items-start">
                                                                                <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">•</span>
                                                                                <Text className="flex-1 leading-relaxed text-sm">{item.text}</Text>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <Text className="text-neutral-900 opacity-60 text-sm">No ingredients listed.</Text>
                                                            )}
                                                        </div>

                                                        {/* Prerequisites with Component Headers (Desktop) */}
                                                        {combinedData.prerequisites.length > 0 && (
                                                            <div>
                                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">PREREQUISITES</div>
                                                                <div className="space-y-2">
                                                                    {combinedData.prerequisites.map((item, index) => {
                                                                        if (item.type === 'header') {
                                                                            return (
                                                                                <div key={index} className="mt-3 first:mt-0">
                                                                                    <Text className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                                        {item.text}
                                                                                    </Text>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <div key={index} className="flex items-start">
                                                                                <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">•</span>
                                                                                <Text className="flex-1 leading-relaxed text-sm">{item.text}</Text>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Steps with Component Headers (Desktop) */}
                                                        <div>
                                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">INSTRUCTIONS</div>
                                                            {combinedData.steps.length > 0 ? (
                                                                <div className="space-y-3">
                                                                    {combinedData.steps.map((item, index) => {
                                                                        if (item.type === 'header') {
                                                                            return (
                                                                                <div key={index} className="mt-4 first:mt-0">
                                                                                    <Text className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                                        {item.text}
                                                                                    </Text>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        // Count only item steps for numbering
                                                                        const stepNumber = combinedData.steps.slice(0, index + 1).filter(s => s.type === 'item').length;
                                                                        return (
                                                                            <div key={index} className="flex items-start">
                                                                                <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                                    {stepNumber}
                                                                                </span>
                                                                                <Text className="flex-1 leading-relaxed text-sm">{item.text}</Text>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <Text className="text-neutral-900 opacity-60 text-sm">No instructions provided.</Text>
                                                            )}
                                                        </div>
                                                    </>
                                                );
                                            }
                                            return false; // Return false to trigger the else branch
                                        })() || (
                                            <>
                                                {/* Simple Recipe Display (Desktop) */}
                                        {/* Ingredients */}
                                        <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">INGREDIENTS</div>
                                            {recipe.ingredients && recipe.ingredients.length > 0 ? (
                                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-4 gap-y-2">
                                                    {recipe.ingredients.map((ingredient, index) => (
                                                        <div key={index} className="flex items-start">
                                                            <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">•</span>
                                                            <Text className="flex-1 leading-relaxed text-sm">{ingredient}</Text>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <Text className="text-neutral-900 opacity-60 text-sm">No ingredients listed.</Text>
                                            )}
                                        </div>

                                        {/* Prerequisites */}
                                        {recipe.instructions && recipe.instructions.prerequisites && recipe.instructions.prerequisites.length > 0 && (
                                            <div>
                                                <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">PREREQUISITES</div>
                                                <div className="space-y-2">
                                                    {recipe.instructions.prerequisites.map((prereq, index) => (
                                                        <div key={index} className="flex items-start">
                                                            <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">•</span>
                                                            <Text className="flex-1 leading-relaxed text-sm">{prereq}</Text>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Instructions */}
                                        <div>
                                            <div className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60 mb-3">INSTRUCTIONS</div>
                                            
                                            {/* Live Session Instructions - Priority 1 */}
                                            {(() => {
                                                try {
                                                    if (sessionData?.instructions) {
                                                        const instructions = typeof sessionData.instructions === 'string' 
                                                            ? JSON.parse(sessionData.instructions) 
                                                            : sessionData.instructions;
                                                        
                                                        if (instructions && instructions.steps && instructions.steps.length > 0) {
                                                            return (
                                                                <div className="space-y-3">
                                                                    {instructions.steps.map((step, index) => (
                                                                        <div key={index} className="flex items-start">
                                                                            <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                                {index + 1}
                                                                            </span>
                                                                            <div className="flex-1">
                                                                                <Text className="leading-relaxed text-neutral-900 opacity-80 text-sm">{step.body}</Text>
                                                                                {step.durationSec && (
                                                                                    <Text className="text-xs text-neutral-900 opacity-60 mt-1">
                                                                                        {Math.floor(step.durationSec / 60)}:{(step.durationSec % 60).toString().padStart(2, '0')} min
                                                                                    </Text>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        }
                                                    }
                                                } catch (e) {
                                                    console.error('Error parsing live session instructions:', e);
                                                }
                                                return null;
                                            })()}
                                            
                                            {recipe.instructions && typeof recipe.instructions === 'object' ? (
                                                <div>
                                                    {/* Cooking Steps */}
                                                    {recipe.instructions.steps && recipe.instructions.steps.length > 0 && (
                                                        <div className="space-y-3">
                                                            {recipe.instructions.steps.map((step, index) => {
                                                                // Get timing segments for this step from steps_with_timing
                                                                const stepWithTiming = recipe.steps_with_timing && Array.isArray(recipe.steps_with_timing) 
                                                                    ? recipe.steps_with_timing[index] 
                                                                    : null;
                                                                const timingSegments = stepWithTiming?.timing_segments || [];
                                                                
                                                                // Check if this step has any timers
                                                                const hasTimers = timingSegments.some(s => s.expectedSeconds > 0);
                                                                
                                                                return (
                                                                    <div key={index} className="flex items-start">
                                                                        <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                            {index + 1}
                                                                        </span>
                                                                        <div className="flex-1 leading-relaxed text-sm relative">
                                                                            <div className="flex items-start gap-2">
                                                                                <Text as="span" className="flex-1">{step}</Text>
                                                                                {hasTimers && (
                                                                                    <span 
                                                                                        className="text-xs font-mono flex-shrink-0 relative z-10"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            if (openTimerPopover?.stepIndex === index && openTimerPopover?.section === 'desktop') {
                                                                                                setOpenTimerPopover(null);
                                                                                            } else {
                                                                                                setOpenTimerPopover({ stepIndex: index, stepText: step, section: 'desktop' });
                                                                                            }
                                                                                        }}
                                                                                        style={{ cursor: 'pointer', color: '#0D0D0D', opacity: 0.4 }}
                                                                                    >
                                                                                        ⏱
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {hasTimers && openTimerPopover?.stepIndex === index && openTimerPopover?.section === 'desktop' && (
                                                                                <StepTimerPopover
                                                                                    timingSegments={timingSegments}
                                                                                    stepText={step}
                                                                                    stepNumber={index + 1}
                                                                                    onClose={() => setOpenTimerPopover(null)}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <Text className="text-neutral-900 opacity-60 text-sm">No instructions provided.</Text>
                                            )}
                                        </div>
                                            </>
                                        )}

                                        {/* Propose Merge Button - Only show for forks where user is the owner */}
                                        {!recipe.is_original && recipe.owner_id === user?.uid && (
                                            <div className="mt-6 p-4 bg-neutral-50 hairline">
                                                <div className="text-center">
                                                    <Text className="text-sm font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-3">
                                                        SUGGEST CHANGES
                                                    </Text>
                                                    <button
                                                        onClick={handleProposeMerge}
                                                        className="px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all border-none text-white hover:opacity-60"
                                                        style={{ backgroundColor: colorTokens.oxfordBlue }}
                                                        disabled={customizing}
                                                    >
                                                        {customizing ? 'SUBMITTING...' : (isOnlyContributor() ? 'UPDATE GOLD STANDARD' : 'PROPOSE GOLD STANDARD CHANGE')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Merge Proposal Success Message */}
                                        {mergeProposalSuccess && (
                                            <div className="mt-4 p-4 bg-green-50 border border-green-200">
                                        <Text className="text-green-900 text-sm">
                                            {isOnlyContributor() ? '✅ Gold Standard updated successfully!' : '✅ Gold Standard change proposal submitted successfully! The recipe owner will be notified.'}
                                        </Text>
                                        </div>
                                        )}

                                    </div>

                                        {/* Fade effect overlay - sticky to bottom, indicates more content below */}
                                    <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-neutral-0 to-transparent pointer-events-none z-10"></div>
                                </div>
                            </div>

                            {/* Right Section - Image (40%, Sticky) - Not in scrollable area */}
                            <div className="flex-[0_0_40%] flex-shrink-0">
                                <div className="sticky w-full" style={{ top: '1.5rem', paddingRight: '1.5rem', paddingBottom: '1.5rem' }}>
                                    <div 
                                        className={`relative w-full overflow-hidden hairline group ${
                                            imageDragOver === currentImageIndex ? 'ring-2 ring-neutral-900' : ''
                                        }`}
                                        style={{ height: 'calc(80vh - 4rem)' }}
                                        onDrop={(e) => isOwner && handleImageDrop(e, currentImageIndex)}
                                        onDragOver={(e) => isOwner && handleImageDragOver(e, currentImageIndex)}
                                        onDragLeave={handleImageDragLeave}
                                    >
                                    <SignedImage
                                        src={recipe.images && recipe.images.length > 0 ? recipe.images[currentImageIndex] : null}
                                        alt={`${recipe.name || recipe.title} - Image ${currentImageIndex + 1}`}
                                        className="w-full h-full object-cover"
                                        category={recipe.category}
                                    />
                                    
                                    {/* Edit Button - Show on hover for owners */}
                                    {isOwner && (
                                        <button
                                            onClick={() => handleImageFileSelect(currentImageIndex)}
                                            disabled={updatingImage}
                                            className="absolute bottom-2 left-2 w-8 h-8 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100 z-10"
                                            title="Replace image"
                                        >
                                            ✎
                                        </button>
                                    )}

                                    {/* Navigation Arrows - Only show if multiple images */}
                                    {recipe.images && recipe.images.length > 1 && (
                                        <>
                                            <button
                                                onClick={prevImage}
                                                className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-neutral-0 text-neutral-900 hover:bg-neutral-50 transition-all duration-200 flex items-center justify-center hairline"
                                                aria-label="Previous image"
                                            >
                                                ←
                                            </button>
                                            <button
                                                onClick={nextImage}
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-neutral-0 text-neutral-900 hover:bg-neutral-50 transition-all duration-200 flex items-center justify-center hairline"
                                                aria-label="Next image"
                                            >
                                                →
                                            </button>
                                        </>
                                    )}

                                    {/* Image Counter */}
                                    {recipe.images && recipe.images.length > 1 && (
                                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-50 text-white text-xs font-mono">
                                            {currentImageIndex + 1} / {recipe.images.length}
                                        </div>
                                    )}
                                    </div>
                                </div>

                                {/* Image Indicators/Thumbnails - Only show if multiple images */}
                                {recipe.images && recipe.images.length > 1 && (
                                    <div className="flex justify-center mt-4 space-x-2">
                                        {recipe.images.map((image, index) => (
                                            <div
                                                key={index}
                                                className={`relative w-12 h-12 overflow-hidden hairline transition-opacity duration-200 group ${
                                                    index === currentImageIndex ? 'opacity-100 ring-2 ring-neutral-900' : 'opacity-60 hover:opacity-80'
                                                } ${
                                                    imageDragOver === index ? 'ring-2 ring-neutral-900' : ''
                                                }`}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (isOwner) {
                                                        handleImageDrop(e, index);
                                                    }
                                                }}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (isOwner) {
                                                        handleImageDragOver(e, index);
                                                    }
                                                }}
                                                onDragLeave={handleImageDragLeave}
                                            >
                                                <button
                                                    onClick={() => goToImage(index)}
                                                    className="w-full h-full"
                                                >
                                                    <SignedImage
                                                        src={image}
                                                        alt={`${recipe.name || recipe.title} - Thumbnail ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                        category={recipe.category}
                                                    />
                                                </button>
                                                {/* Edit Button - Show on hover for owners */}
                                                {isOwner && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleImageFileSelect(index);
                                                        }}
                                                        disabled={updatingImage}
                                                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 z-10"
                                                        title="Replace image"
                                                    >
                                                        <span className="text-white text-lg">✎</span>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Action Buttons - Below the card */}
                <div className="max-w-5xl mx-auto mt-6">
                        <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-4">
                        {!user && (
                            <div className="flex space-x-4">
                                <Link 
                                    to="/login"
                                    className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                >
                                    LOGIN TO START SESSION
                                </Link>
                            </div>
                        )}
                        </div>
                    </div>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    title="Delete Recipe"
                >
                    <div className="vspace">
                        <Text>
                            Are you sure you want to delete "{recipe.name || recipe.title}"? This action cannot be undone.
                        </Text>
                        
                        <div className="flex space-x-4 pt-4">
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                DELETE
                            </button>
                            <button
                                onClick={() => setDeleteModalOpen(false)}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Session Reflection Modal */}
                <Modal
                    isOpen={reflectionModalOpen}
                    onClose={() => setReflectionModalOpen(false)}
                    title="Session Reflections"
                >
                    {(() => {
                        try {
                            const reflection = recipe.reflection ? JSON.parse(recipe.reflection) : null;
                            if (reflection) {
                                const sessionData = reflection.sessionData;
                                return (
                                    <div className="vspace">
                                        {/* Session Info - Only visible to recipe owners */}
                                        {isOwner && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Session Details</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <div className="space-y-2 text-sm">
                                                        {sessionData?.completedAt && (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-neutral-900 opacity-60">Completed:</span>
                                                                <span className="text-neutral-900 font-mono">
                                                                    {new Date(sessionData.completedAt).toLocaleString()}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {sessionData?.totalElapsedTime && (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-neutral-900 opacity-60">Duration:</span>
                                                                <span className="text-neutral-900 font-mono">
                                                                    {Math.floor(sessionData.totalElapsedTime / 60)}:{(sessionData.totalElapsedTime % 60).toString().padStart(2, '0')} minutes
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Reflections */}
                                        {reflection.comments && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Comments</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <Text className="leading-relaxed">{reflection.comments}</Text>
                                                </div>
                                            </div>
                                        )}

                                        {reflection.ingredientNotes && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Ingredient Notes</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <Text className="leading-relaxed">{reflection.ingredientNotes}</Text>
                                                </div>
                                            </div>
                                        )}

                                        {reflection.improvementNotes && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Improvement Notes</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <Text className="leading-relaxed">{reflection.improvementNotes}</Text>
                                                </div>
                                            </div>
                                        )}

                                        {!reflection.comments && !reflection.ingredientNotes && !reflection.improvementNotes && (
                                            <div className="text-center py-8">
                                                <Text className="text-neutral-900 opacity-60">No reflections were written for this session.</Text>
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        } catch (e) {
                            console.error('Error displaying reflection data:', e);
                        }
                        return (
                            <div className="text-center py-8">
                                <Text className="text-neutral-900 opacity-60">No reflection data available.</Text>
                            </div>
                        );
                    })()}
                </Modal>

                {/* Cooking Calendar Modal */}
                <CookingCalendar
                    isOpen={calendarModalOpen}
                    onClose={() => setCalendarModalOpen(false)}
                    kernelId={recipe.kernel_id}
                />

                {/* Start Cooking Modal - Redesigned Layout */}
                <Modal 
                    isOpen={showCustomizeModal} 
                    onClose={() => setShowCustomizeModal(false)} 
                    title=""
                    backdropOpacity={0}
                    maxWidth="max-w-[1150px]"
                    className="p-0"
                    hideCloseButton={true}
                >
                    <div className="bg-neutral-0 relative w-full overflow-x-hidden">
                        {/* Close Button - Floating Top Right */}
                        <button 
                            onClick={() => setShowCustomizeModal(false)}
                            className="absolute top-4 right-4 z-10 w-8 h-8 bg-neutral-0 hairline flex items-center justify-center text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-all duration-120 font-mono text-lg p-0 m-0"
                        >
                            ✕
                        </button>

                        {/* Main Content - Ingredients (60%) | Image (40%) */}
                        <div className="flex flex-col min-650-flex-row gap-6 relative overflow-x-hidden" style={{ minHeight: '400px', maxHeight: '500px', paddingRight: '1.5rem' }}>
                            {/* Left Section - Ingredients (60%, Scrollable) */}
                            <div className="flex-[0_0_60%] w-full min-650-w-auto overflow-y-auto overflow-x-hidden no-scrollbar pr-2" style={{ maxHeight: '500px' }}>
                                <div className="p-6 space-y-6">
                                    {/* Recipe Title */}
                                    <div>
                                        <input
                                            type="text"
                                            value={customRecipeName}
                                            onChange={(e) => setCustomRecipeName(e.target.value)}
                                            className="font-mono tracking-tight bg-transparent border-none outline-none focus:bg-neutral-50 px-2 -ml-2 w-full mb-2"
                                            style={{ color: colorTokens.oxfordBlue, fontSize: '2.25rem', lineHeight: '1.2' }}
                                            placeholder="Recipe name"
                                        />
                                    <div className="border-t mb-3" style={{ borderColor: colorTokens.red }}></div>
                                    <div className="flex items-center space-x-4 text-sm font-mono text-neutral-900 opacity-60">
                                        <span>{recipe.category || 'RECIPE'}</span>
                                        <span>•</span>
                                        <span>BY {recipe.author || recipe.creator_name || 'UNKNOWN'}</span>
                                    </div>
                                </div>
                                
                                        {/* Ingredients Section - Conditional: Multi-Component vs Simple */}
                                        {customIsMultiComponent ? (
                                            <div>
                                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                                    INGREDIENTS
                                                </div>
                                                <div className="space-y-4">
                                                    {customComponents.map((component, compIndex) => (
                                                        <div key={compIndex} className="space-y-3">
                                                            {/* Component Header */}
                                                            <div className="font-mono text-sm font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                                                                For the {component.name || `Component ${compIndex + 1}`}:
                                                            </div>
                                                            
                                                            {/* Component Ingredients */}
                                                            <div className="space-y-2 ml-4">
                                                                {(component.ingredients || []).map((ingredient, index) => (
                                                                    <div key={index} className="flex items-start">
                                                                        <span className="font-mono text-xs text-neutral-900 opacity-60 mr-2 flex-shrink-0 mt-1">•</span>
                                                                        <textarea
                                                                            value={ingredient}
                                                                            onChange={(e) => {
                                                                                const newComponents = [...customComponents];
                                                                                const newIngredients = [...(newComponents[compIndex].ingredients || [])];
                                                                                newIngredients[index] = e.target.value;
                                                                                newComponents[compIndex] = { ...newComponents[compIndex], ingredients: newIngredients };
                                                                                setCustomComponents(newComponents);
                                                                            }}
                                                                            placeholder="Enter ingredient with quantity"
                                                                            rows={1}
                                                                            className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                                            style={{ height: 'auto', minHeight: '20px' }}
                                                                            onInput={(e) => {
                                                                                e.target.style.height = 'auto';
                                                                                e.target.style.height = e.target.scrollHeight + 'px';
                                                                            }}
                                                                        />
                                                                        {(component.ingredients || []).length > 1 && (
                                                                            <button 
                                                                                onClick={() => {
                                                                                    const newComponents = [...customComponents];
                                                                                    const newIngredients = (newComponents[compIndex].ingredients || []).filter((_, i) => i !== index);
                                                                                    newComponents[compIndex] = { ...newComponents[compIndex], ingredients: newIngredients };
                                                                                    setCustomComponents(newComponents);
                                                                                }}
                                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                                            >
                                                                                ✕
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <button 
                                                                onClick={() => {
                                                                    const newComponents = [...customComponents];
                                                                    const newIngredients = [...(newComponents[compIndex].ingredients || []), ''];
                                                                    newComponents[compIndex] = { ...newComponents[compIndex], ingredients: newIngredients };
                                                                    setCustomComponents(newComponents);
                                                                }}
                                                                className="w-full ml-4 mt-2 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                                            >
                                                                + ADD INGREDIENT
                                                            </button>
                                                            
                                                            {compIndex < customComponents.length - 1 && (
                                                                <div className="border-t my-3" style={{ borderColor: colorTokens.notecardBlue }}></div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                        <div>
                                            <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-4">
                                                INGREDIENTS
                                            </div>
                                            <div className="space-y-3">
                                                {customIngredients.map((ingredient, index) => (
                                                    <div key={index} className="flex items-start">
                                                        <span className="font-mono text-sm text-neutral-900 opacity-60 mr-3 flex-shrink-0 mt-1">
                                                            {index + 1}.
                                                        </span>
                                                        <textarea
                                                            value={ingredient}
                                                            onChange={(e) => {
                                                                const newArr = [...customIngredients];
                                                                newArr[index] = e.target.value;
                                                                setCustomIngredients(newArr);
                                                            }}
                                                            placeholder="Enter ingredient with quantity (e.g., 2 cups flour)"
                                                            rows={1}
                                                            className="flex-1 px-3 py-2 hairline bg-neutral-0 focus:outline-none focus:bg-neutral-50 resize-none text-sm"
                                                            style={{ 
                                                                height: 'auto',
                                                                minHeight: '20px'
                                                            }}
                                                            onInput={(e) => {
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = e.target.scrollHeight + 'px';
                                                            }}
                                                        />
                                                        {customIngredients.length > 1 && (
                                                            <button
                                                                onClick={() => setCustomIngredients(customIngredients.filter((_, i) => i !== index))}
                                                                className="w-6 h-6 bg-red-600 text-white text-xs hover:bg-red-700 transition-colors ml-2 flex-shrink-0 mt-1"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            <button 
                                                onClick={() => setCustomIngredients([...customIngredients, ''])} 
                                                className="w-full mt-4 px-3 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-xs uppercase tracking-wider"
                                            >
                                                + ADD INGREDIENT
                                            </button>
                                        </div>
                                        )}
                                    </div>
                                </div>

                                {/* Right Section - Image (40%, Top Right on desktop >650px, Bottom on mobile) */}
                                <div className="flex-[0_0_40%] w-full min-650-w-auto flex-shrink-0 overflow-hidden order-2 min-650-order-1">
                                    <div className="w-full pt-6 pr-6 pb-6 min-650-pb-0">
                                        <div className="relative w-full overflow-hidden hairline min-650-hidden" style={{ height: '200px', width: '100%' }}>
                                        <SignedImage
                                            src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                            alt={`${recipe.name || recipe.title}`}
                                            className="w-full h-full object-cover"
                                            category={recipe.category}
                                        />
                                    </div>
                                        <div className="hidden min-650-block relative w-full overflow-hidden hairline" style={{ height: 'calc(500px - 3rem)', width: '100%' }}>
                                            <SignedImage
                                                src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                                                alt={`${recipe.name || recipe.title}`}
                                                className="w-full h-full object-cover"
                                                category={recipe.category}
                                            />
                                </div>
                            </div>
                                        </div>
                            </div>

                        {/* Bottom Bar - Full Width with Start Cooking Button */}
                        <div className="p-6" style={{ borderTop: '1px solid #0D0D0D', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}>
                                                <button 
                                onClick={handleConfirmCustomization} 
                                disabled={customizing} 
                                className="w-full px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all border-none text-white hover:opacity-60 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: colorTokens.oxfordBlue }}
                            >
                                {customizing ? 'STARTING...' : 'START COOKING'}
                                                </button>
                                                </div>
                                            </div>
                </Modal>


                {/* Session Reflection Modal for any session */}
                <Modal
                    isOpen={showSessionReflectionModal}
                    onClose={() => setShowSessionReflectionModal(false)}
                    title="Session Reflection"
                >
                    {selectedReflectionSession ? (
                        (() => {
                            // Access individual reflection fields from the session
                            const comments = selectedReflectionSession.reflection_comments;
                            const ingredientNotes = selectedReflectionSession.reflection_ingredient_notes;
                            const improvementNotes = selectedReflectionSession.reflection_improvement_notes;
                            const sessionPhotos = selectedReflectionSession.session_photos;
                            const sessionInstructionsUsed = selectedReflectionSession.session_instructions_used;
                            const sessionStartTime = selectedReflectionSession.session_start_time;
                            const sessionEndTime = selectedReflectionSession.session_end_time;
                            
                            // Check if any reflection data exists
                            const hasReflectionData = comments || ingredientNotes || improvementNotes;
                            
                            if (hasReflectionData) {
                                return (
                                    <div className="vspace">
                                        {/* Session Info - Only visible to recipe owners */}
                                        {isOwner && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Session Details</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <div className="space-y-2 text-sm">
                                                        {sessionEndTime && (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-neutral-900 opacity-60">Completed:</span>
                                                                <span className="text-neutral-900 font-mono">
                                                                    {new Date(sessionEndTime).toLocaleString()}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {selectedReflectionSession.actual_duration_seconds && (
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-neutral-900 opacity-60">Duration:</span>
                                                                <span className="text-neutral-900 font-mono">
                                                                    {Math.floor(selectedReflectionSession.actual_duration_seconds / 60)}:{(selectedReflectionSession.actual_duration_seconds % 60).toString().padStart(2, '0')} minutes
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Session Instructions Used */}
                                        {sessionInstructionsUsed && Array.isArray(sessionInstructionsUsed) && sessionInstructionsUsed.length > 0 && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Instructions Used</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <div className="space-y-2">
                                                        {sessionInstructionsUsed.map((instruction, index) => (
                                                            <div key={index} className="flex items-start">
                                                                <span className="font-mono text-sm font-bold text-neutral-900 mr-3 flex-shrink-0 mt-1">
                                                                    {index + 1}
                                                                </span>
                                                                <Text className="text-sm leading-relaxed">{instruction}</Text>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Reflections */}
                                        {comments && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Comments</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <Text className="leading-relaxed">{comments}</Text>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {ingredientNotes && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Ingredient Notes</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <Text className="leading-relaxed">{ingredientNotes}</Text>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {improvementNotes && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Improvement Notes</Heading>
                                                <div className="bg-neutral-50 hairline p-4">
                                                    <Text className="leading-relaxed">{improvementNotes}</Text>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Session Photos */}
                                        {sessionPhotos && Array.isArray(sessionPhotos) && sessionPhotos.length > 0 && (
                                            <div className="mb-6">
                                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Session Photos</Heading>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    {sessionPhotos.map((photo, index) => (
                                                        <div key={index} className="relative">
                                                            <SignedImage
                                                                src={photo}
                                                                alt={`Session photo ${index + 1}`}
                                                                className="w-full h-32 object-cover hairline"
                                                                category={recipe.category}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {!comments && !ingredientNotes && !improvementNotes && (
                                            <div className="text-center py-8">
                                                <Text className="text-neutral-900 opacity-60">No reflections were written for this session.</Text>
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            
                            return (
                                <div className="text-center py-8">
                                    <Text className="text-neutral-900 opacity-60">No reflection data available.</Text>
                                </div>
                            );
                        })()
                    ) : (
                        <div className="text-center py-8">
                            <Text className="text-neutral-900 opacity-60">No reflection data available.</Text>
                        </div>
                    )}
                </Modal>

                {/* Add to Collection Modal */}
                <Modal
                    isOpen={showAddToCollection}
                    onClose={() => setShowAddToCollection(false)}
                    title="Add to Collection"
                >
                    <div className="space-y-4">
                        <Text>Select a collection to add this recipe to:</Text>
                        <div className="space-y-2">
                            {collections.length > 0 ? (
                                collections.map((collection) => (
                                    <button
                                        key={collection.id}
                                        onClick={() => addToCollection(collection.id)}
                                        className="w-full p-3 text-left bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-sm uppercase tracking-wider"
                                    >
                                        {collection.name}
                                    </button>
                                ))
                            ) : (
                                <Text className="text-neutral-900 opacity-60">
                                    No collections available. Create collections in your recipe library.
                                </Text>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <button
                                onClick={() => setShowAddToCollection(false)}
                                className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-sm uppercase tracking-wider"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Merge Proposal Modal */}
                <MergeProposalModal
                    isOpen={showMergeProposalModal}
                    onClose={() => setShowMergeProposalModal(false)}
                    sourceRecipe={recipe}
                    onProposalCreated={handleProposalCreated}
                />

                {/* Update Gold Standard Confirmation Modal */}
                <Modal
                    isOpen={showUpdateGoldStandardModal}
                    onClose={() => setShowUpdateGoldStandardModal(false)}
                    title="Update Gold Standard"
                >
                    <div className="space-y-6">
                        <div className="text-center">
                            <Text className="text-lg font-medium text-neutral-900 mb-4">
                                Are you sure you want to update the Gold Standard?
                            </Text>
                            <Text className="text-sm text-neutral-900 opacity-80 mb-6">
                                This will make your version the new Gold Standard for this recipe. 
                                The original will remain in the lineage for reference.
                            </Text>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowUpdateGoldStandardModal(false)}
                                className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-sm uppercase tracking-wider"
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        const token = await user.getIdToken();
                                        console.log('Updating gold standard for recipe:', recipe.id);
                                        
                                        const { updateGoldStandard } = await import('../services/api');
                                        await updateGoldStandard(recipe.id, token);
                                        
                                        setShowUpdateGoldStandardModal(false);
                                        // Show success message
                                        setMergeProposalSuccess(true);
                                        setTimeout(() => {
                                            setMergeProposalSuccess(false);
                                        }, 5000);
                                        
                                        // Refresh the recipe data to show updated Gold Standard
                                        fetchRecipeDetails();
                                    } catch (error) {
                                        console.error('Error updating Gold Standard:', error);
                                        alert('Failed to update Gold Standard. Please try again.');
                                    }
                                }}
                                className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none text-white hover:opacity-60"
                                style={{ backgroundColor: colorTokens.oxfordBlue }}
                            >
                                UPDATE GOLD STANDARD
                            </button>
                        </div>
                    </div>
                </Modal>
                    </>
                )}
            </div>
        </div>
        <>
            {/* Recipe Card Overlay */}
            {showRecipeCardOverlay && recipe && (
                <RecipeCardOverlay
                    recipe={recipe}
                    onClose={() => setShowRecipeCardOverlay(false)}
                />
            )}
            
            {/* Education Modals */}
            <EducationModal
                isOpen={showStartSessionModal}
                onClose={() => setShowStartSessionModal(false)}
                title="Start Live Session"
                description="Begin a live cooking session with step-by-step guidance, timers, and voice assistance. Customize ingredients and instructions before starting."
                image="/images/education/start-session-demo.png"
                details={[
                    "Get real-time cooking guidance and timers",
                    "Customize ingredients and instructions before starting",
                    "Use voice commands to navigate through steps",
                    "Track your progress and cooking time",
                    "Save session data for future reference"
                ]}
            />
            <EducationModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                title="Edit Recipe"
                description="Modify your recipe's ingredients, instructions, images, and other details. Changes will be saved to your recipe."
                image="/images/education/edit-demo.png"
                details={[
                    "Update recipe name, description, and category",
                    "Modify ingredients and quantities",
                    "Edit cooking instructions and steps",
                    "Change images and metadata",
                    "Save changes to your recipe"
                ]}
            />
            <EducationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Delete Recipe"
                description="Permanently remove this recipe from your collection. This action cannot be undone."
                image="/images/education/delete-demo.png"
                details={[
                    "Permanently removes the recipe from your collection",
                    "Cannot be undone once deleted",
                    "All associated data will be removed",
                    "Consider backing up important recipes before deleting"
                ]}
            />
            <EducationModal
                isOpen={showLineageEducationModal}
                onClose={() => setShowLineageEducationModal(false)}
                title="Recipe Lineage"
                description="View the history and evolution of this recipe. See how it has been forked and modified over time."
                image="/images/education/lineage-demo.png"
                details={[
                    "View the complete history of recipe changes",
                    "See all forks and variations of the recipe",
                    "Track how the recipe has evolved",
                    "Understand the recipe's relationship to other versions"
                ]}
            />
            <EducationModal
                isOpen={showCalendarEducationModal}
                onClose={() => setShowCalendarEducationModal(false)}
                title="Cooking Calendar"
                description="View all cooking sessions for this recipe technique. See when you've cooked this recipe and track your progress."
                image="/images/education/calendar-demo.png"
                details={[
                    "View all cooking sessions for this technique",
                    "See session dates and details",
                    "Track your cooking history",
                    "Access past session reflections"
                ]}
            />
            
            {/* Floating Tabs */}
            {showCompareModal ? (
                // When on comparison page, only show back tab that goes to lineage
                <FloatingBackTab onBack={() => {
                    closeCompareModal();
                    setShowLineageModal(true);
                    setLineageRefreshKey(prev => prev + 1);
                }} />
            ) : (
                <>
                    {!showLineageModal && !showCustomizeModal && (
                        <FloatingBackTab onBack={() => navigate('/recipes')} />
                    )}
                    {user && (
                        <>
                            {!showLineageModal && (
                            <FloatingLiveSessionTab 
                                onClick={activeSession ? handleStartLiveSession : handleStartLiveSessionClick}
                                disabled={startingSession}
                                isModalOpen={showCustomizeModal}
                                onClose={() => setShowCustomizeModal(false)}
                            />
                            )}
                            <FloatingLineageTab 
                                onClick={() => {
                                    if (showLineageModal) {
                                        // When lineage modal is open, clicking the tab (now showing back arrow) goes to selected recipe
                                        if (selectedLineageRecipe) {
                                            navigate(`/recipes/${selectedLineageRecipe.id}`);
                                            setSelectedLineageRecipe(null);
                                            setShowLineageModal(false);
                                        } else {
                                            // If no recipe selected, go to current recipe
                                            navigate(`/recipes/${id}`);
                                            setShowLineageModal(false);
                                        }
                                    } else {
                                        // When lineage modal is closed, clicking opens it
                                    setShowLineageModal(true);
                                        setSelectedLineageRecipe(null);
                                    setLineageRefreshKey(prev => prev + 1);
                                    }
                                }}
                                isModalOpen={showLineageModal}
                                onClose={() => {
                                    setShowLineageModal(false);
                                    setSelectedLineageRecipe(null);
                                }}
                            />
                            {showLineageModal && (
                                <>
                                    <FloatingCompareTab 
                                        onClick={openCompareSelection}
                                        isCompareMode={lineageCompareMode}
                                        isSelectionModalOpen={showCompareSelectionModal}
                                        onClose={closeCompareSelection}
                                    />
                                    <FloatingSessionsTab 
                                        onClick={() => setShowSessionsModal(true)}
                                        isModalOpen={showSessionsModal}
                                        onClose={() => setShowSessionsModal(false)}
                                    />
                                    <FloatingForkTab 
                                        onClick={handleForkClick}
                                        isModalOpen={showForkModal}
                                        onClose={() => setShowForkModal(false)}
            />
        </>
                            )}
                        </>
                    )}
                </>
            )}
        </>
        
        {/* Compare Selection Modal */}
        <CompareSelectionModal
            isOpen={showCompareSelectionModal}
            onClose={closeCompareSelection}
            selectedRecipes={compareSelectedRecipes}
            onToggleRecipe={toggleRecipeSelection}
        />
        
        {/* Sessions Modal */}
        {/* CRITICAL: Use selectedLineageRecipe.id if in lineage modal with a selected recipe, otherwise use recipe.id */}
        <SessionsModal
            isOpen={showSessionsModal}
            onClose={() => setShowSessionsModal(false)}
            recipeId={showLineageModal && selectedLineageRecipe ? selectedLineageRecipe.id : recipe?.id}
        />
        
        {/* Compare Modal */}
        <RecipeDiffModal
            isOpen={showCompareModal}
            onClose={closeCompareModal}
            originalRecipe={compareRecipes[0] || {}}
            updatedRecipe={compareRecipes[1] || {}}
        />
        
        {/* Attribution Popup for Transferred Recipes */}
        {recipe && recipe.is_transfer && recipe.source_url && showAttributionPopup && (
            <Modal
                isOpen={showAttributionPopup}
                onClose={() => setShowAttributionPopup(false)}
                title=""
                backdropOpacity={0.5}
                maxWidth="max-w-md"
            >
                <div className="space-y-4">
                    <div className="text-center">
                        <div className="inline-block px-2 py-1 bg-neutral-50 hairline">
                            <Text size="xs" className="text-neutral-900 font-mono uppercase tracking-wider">
                                Transferred for personal use
                            </Text>
                        </div>
                    </div>
                    <div 
                        className="p-4"
                        style={{ 
                            backgroundColor: '#F9F8F6',
                            border: 'none',
                            borderWidth: 0,
                            borderStyle: 'none',
                            outline: 'none',
                            boxShadow: 'none'
                        }}
                    >
                        <Text size="xs" className="text-neutral-900 font-mono uppercase tracking-wider mb-2">
                            Source & Attribution
                        </Text>
                        <div className="space-y-1 text-sm">
                            {recipe.author && (
                                <Text size="sm" className="text-neutral-900">
                                    Original Author: {recipe.author}
                                </Text>
                            )}
                            <div>
                                <Text size="sm" className="text-neutral-900">
                                    Original Source:{' '}
                                    <a 
                                        href={recipe.source_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="underline hover:opacity-60"
                                    >
                                        View Original Recipe
                                    </a>
                                </Text>
                            </div>
                            <Text size="xs" className="text-neutral-900 opacity-60 mt-2">
                                Original recipe content (including instructions, descriptions, and photos) belongs to {recipe.author || 'the original website'} and/or the original website.
                                This version was transferred to 4RKED for personal use.
                            </Text>
                        </div>
                    </div>
                </div>
            </Modal>
        )}
        </>
    );
};

export default RecipeDetails; 
